import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VoiceWaveform from './VoiceWaveform';
import QuestionDialog from './QuestionDialog';
import { SpeedReadingLesson, TrainingApi, formatLessonDuration, InteractionAnswerResponse } from './trainingApi';
import { configureSpeedReadingMetronome, pauseSpeedReadingMetronome } from '../MetronomePanel';
import { METRONOME_AUDIO_SRC } from '../modes/speedReadingShared';

interface LessonPlayerProps {
  lesson: SpeedReadingLesson;
  onBack: () => void;
}

const PLAYBACK_RATES = [1, 1.25, 1.5];
const MAX_MEMORY_PEG_COUNT = 100;
const DEFAULT_NUMBER_TRACKING_DURATION_SEC = 60;
const PLAYER_SNAPSHOT_KEY_PREFIX = 'speed-reading-training-player';

type LessonVisualCategory = 'reading' | 'question' | 'memory_pegs' | 'number_tracking' | 'word_alternatives' | 'horizontal_zigzag' | 'three_column_grid' | 'balloon_reading' | 'star_reading' | 'gap_reading' | 'measurement_text';

type LessonInteractionQuiz = {
  id: string;
  timeSec: number;
  segmentIndex?: number;
  sectionTitle?: string;
  question: string;
  options: string[];
  correctOptionIndex: number;
  wrongFeedback?: string;
  feedbackAudioUrl?: string;
};

type LessonVisualMaterial = {
  id?: string;
  title: string;
  pageLabel: string;
  note?: string;
  media?: { id: string; url: string; mimeType?: string } | null;
  visualCategory?: LessonVisualCategory | string;
  measurementTextRole?: 'reading' | 'question' | string;
  isQuestionVisual?: boolean;
  trackTime?: boolean;
  questionCount?: number;
  optionCount?: number;
  correctAnswers?: Array<number | null>;
  memoryPegCount?: number;
  memoryPegLabels?: string[];
  numberTrackingCount?: number;
  numberTrackingDurationSec?: number;
  numberTrackingLineXPercent?: number;
  numberTrackingItems?: NumberTrackingItem[];
  eyeExerciseMode?: string;
  eyeExerciseDurationSec?: number;
  metronomeBpm?: number;
  extractedQuestions?: Array<{
    index: number;
    question: string;
    options: string[];
    correctOptionIndex?: number | null;
  }>;
  wordAlternativeExercises?: WordAlternativeExercise[];
  balloonReadingItems?: BalloonReadingItem[];
  balloonReadingDurationSec?: number;
  starReadingDurationSec?: number;
  gapReadingDurationSec?: number;
};

type LessonVisualCheckpoint = {
  id: string;
  segmentIndex: number;
  title?: string;
  checkpointRole?: string;
  instruction: string;
  requireLastPage?: boolean;
  visualMaterials: LessonVisualMaterial[];
};

type LessonAudioSegment = {
  index: number;
  kind: 'narration' | 'quiz' | 'visual';
  title: string;
  durationSec: number;
  audioUrl: string;
  quiz?: LessonInteractionQuiz;
  visualCheckpoint?: LessonVisualCheckpoint;
};

type VisualQuestionItem = {
  key: string;
  materialTitle: string;
  materialPageLabel: string;
  questionIndex: number;
  question: string;
  options: string[];
  correctOptionIndex: number | null;
};

type NumberTrackingItem = {
  value: number;
  centerXPercent: number;
  centerYPercent: number;
  widthPercent?: number;
  heightPercent?: number;
};

type BalloonReadingItem = {
  index: number;
  rowIndex: number;
  text?: string;
  centerXPercent: number;
  centerYPercent: number;
  widthPercent: number;
  heightPercent: number;
};

type WordAlternativeOption = {
  number: number;
  word: string;
};

type WordAlternativeExercise = {
  index: number;
  label: string;
  sentenceParts: string[];
  options: WordAlternativeOption[];
};

type LessonPlayerSnapshot = {
  activeSegmentIndex: number;
  segmentTime: number;
  currentTime: number;
  rate: number;
  updatedAt: number;
};

type VisualSummaryState = {
  title: string;
  pageTimes: Array<{ title: string; pageLabel: string; seconds: number }>;
  questions: Array<VisualQuestionItem & { selectedOptionIndex: number | null }>;
  memoryPegs: Array<{ materialTitle: string; materialPageLabel: string; label: string; value: string }>;
  wordAlternatives: Array<{
    materialTitle: string;
    materialPageLabel: string;
    label: string;
    answers: string[];
  }>;
  numberTracking: Array<{
    materialTitle: string;
    materialPageLabel: string;
    phase: 1 | 2;
    foundCount: number;
    totalCount: number;
    seconds: number;
  }>;
};

const getLessonVisualCategory = (material: Partial<LessonVisualMaterial> | null): LessonVisualCategory => {
  if (!material) return 'reading';
  if (material.visualCategory === 'question' || material.isQuestionVisual) return 'question';
  if (material.visualCategory === 'memory_pegs') return 'memory_pegs';
  if (material.visualCategory === 'number_tracking') return 'number_tracking';
  if (material.visualCategory === 'word_alternatives') return 'word_alternatives';
  if (material.visualCategory === 'horizontal_zigzag') return 'horizontal_zigzag';
  if (material.visualCategory === 'three_column_grid') return 'three_column_grid';
  if (material.visualCategory === 'balloon_reading') return 'balloon_reading';
  if (material.visualCategory === 'star_reading') return 'star_reading';
  if (material.visualCategory === 'gap_reading') return 'gap_reading';
  if (material.visualCategory === 'measurement_text') return 'measurement_text';
  return 'reading';
};

const hasVisualMaterialSurface = (material: Partial<LessonVisualMaterial> | null) => {
  const cat = getLessonVisualCategory(material);
  return Boolean(
    material?.media?.url ||
    cat === 'horizontal_zigzag' ||
    cat === 'three_column_grid' ||
    cat === 'balloon_reading' ||
    cat === 'star_reading' ||
    cat === 'memory_pegs' ||
    cat === 'number_tracking' ||
    cat === 'word_alternatives'
  );
};

let lastLessonMetronomeTickAt = Number.NEGATIVE_INFINITY;

const playLessonMetronomeTick = (
  tickAudioRef: React.MutableRefObject<HTMLAudioElement | null>,
  intervalMs: number
) => {
  if (typeof window === 'undefined') return;
  const now = window.performance.now();
  const duplicateGuardMs = Math.max(140, Math.min(220, intervalMs * 0.55));
  if (now - lastLessonMetronomeTickAt < duplicateGuardMs) return;
  lastLessonMetronomeTickAt = now;

  if (!tickAudioRef.current) {
    tickAudioRef.current = new Audio(METRONOME_AUDIO_SRC);
    tickAudioRef.current.preload = 'auto';
  }
  tickAudioRef.current.pause();
  tickAudioRef.current.currentTime = 0;
  tickAudioRef.current.play().catch(() => undefined);
};

export const getLessonPlayerSnapshotKey = (lessonId: string) => `${PLAYER_SNAPSHOT_KEY_PREFIX}:${lessonId}`;

const readLessonPlayerSnapshot = (lessonId: string): LessonPlayerSnapshot | null => {
  try {
    const raw = typeof window !== 'undefined' ? window.sessionStorage.getItem(getLessonPlayerSnapshotKey(lessonId)) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const activeSegmentIndex = Number(parsed?.activeSegmentIndex);
    const segmentTime = Number(parsed?.segmentTime);
    const currentTime = Number(parsed?.currentTime);
    const rate = Number(parsed?.rate);
    if (!Number.isFinite(activeSegmentIndex) || !Number.isFinite(segmentTime)) return null;
    return {
      activeSegmentIndex: Math.max(0, Math.floor(activeSegmentIndex)),
      segmentTime: Math.max(0, segmentTime),
      currentTime: Number.isFinite(currentTime) ? Math.max(0, currentTime) : 0,
      rate: PLAYBACK_RATES.includes(rate) ? rate : 1,
      updatedAt: Number(parsed?.updatedAt) || Date.now(),
    };
  } catch {
    return null;
  }
};

const writeLessonPlayerSnapshot = (lessonId: string, snapshot: LessonPlayerSnapshot | null) => {
  if (!snapshot || typeof window === 'undefined') return;
  window.sessionStorage.setItem(getLessonPlayerSnapshotKey(lessonId), JSON.stringify(snapshot));
};

const getMemoryPegLabels = (material: Partial<LessonVisualMaterial> | null): string[] => {
  if (!material || getLessonVisualCategory(material) !== 'memory_pegs') return [];
  const labels = Array.isArray(material.memoryPegLabels)
    ? material.memoryPegLabels.map((label) => String(label || '').trim()).filter(Boolean)
    : [];
  if (labels.length) return labels.slice(0, MAX_MEMORY_PEG_COUNT);
  const count = Math.max(0, Math.min(MAX_MEMORY_PEG_COUNT, Number(material.memoryPegCount) || 0));
  return Array.from({ length: count }).map((_, index) => String(index + 1));
};

const getNumberTrackingItems = (material: Partial<LessonVisualMaterial> | null): NumberTrackingItem[] => {
  if (!material || getLessonVisualCategory(material) !== 'number_tracking') return [];
  const seen = new Set<number>();
  return (Array.isArray(material.numberTrackingItems) ? material.numberTrackingItems : [])
    .map((item) => ({
      value: Number(item?.value),
      centerXPercent: Number(item?.centerXPercent),
      centerYPercent: Number(item?.centerYPercent),
      widthPercent: Number(item?.widthPercent) || 5,
      heightPercent: Number(item?.heightPercent) || 5,
    }))
    .filter((item) => (
      Number.isInteger(item.value) &&
      item.value > 0 &&
      Number.isFinite(item.centerXPercent) &&
      Number.isFinite(item.centerYPercent)
    ))
    .sort((a, b) => a.value - b.value)
    .filter((item) => {
      if (seen.has(item.value)) return false;
      seen.add(item.value);
      return true;
    });
};

const getNumberTrackingCheckpointRound = (checkpoint: Partial<LessonVisualCheckpoint> | null): 1 | 2 => {
  const role = String(checkpoint?.checkpointRole || '').trim();
  return role === 'number_tracking_round2' ? 2 : 1;
};

const isNumberTrackingVisualSegment = (segment: Partial<LessonAudioSegment> | null | undefined): boolean => (
  Boolean(
    segment?.kind === 'visual' &&
    getLessonVisualCategory(segment.visualCheckpoint?.visualMaterials?.[0] || null) === 'number_tracking'
  )
);

const getTimelineSegmentDuration = (segment: Partial<LessonAudioSegment> | null | undefined): number => (
  isNumberTrackingVisualSegment(segment) ? 0 : Math.max(0, Number(segment?.durationSec) || 0)
);

const extractHorizontalZigzagBpm = (checkpoint: LessonVisualCheckpoint | null, material: LessonVisualMaterial | null): number => {
  const text = [
    checkpoint?.checkpointRole,
    checkpoint?.instruction,
    checkpoint?.title,
    material?.title,
    material?.note,
  ].map((value) => String(value || '')).join(' ');
  const match = text.match(/\b(120|150|180)\b/);
  if (match) return Number(match[1]);
  const materialBpm = Number(material?.metronomeBpm);
  return Number.isFinite(materialBpm) && materialBpm > 0 ? materialBpm : 120;
};

const extractLessonMetronomeBpm = (checkpoint: LessonVisualCheckpoint | null, material: LessonVisualMaterial | null, fallback = 120): number => {
  const text = [
    checkpoint?.checkpointRole,
    checkpoint?.instruction,
    checkpoint?.title,
    material?.title,
    material?.note,
  ].map((value) => String(value || '')).join(' ');
  const match = text.match(/\b(40|50|60|120|150|180)\b/);
  if (match) return Number(match[1]);
  const materialBpm = Number(material?.metronomeBpm);
  return Number.isFinite(materialBpm) && materialBpm > 0 ? materialBpm : fallback;
};

const getHorizontalZigzagDurationSec = (material: LessonVisualMaterial | null): number => (
  Math.max(30, Math.min(300, Number(material?.eyeExerciseDurationSec) || 120))
);

const createTrainingHorizontalZigzagNodes = (pointCount = 7) => {
  const safeCount = Math.max(4, Math.min(12, Math.round(pointCount)));
  const left = 120;
  const right = 880;
  const top = 120;
  const bottom = 880;
  const step = (bottom - top) / Math.max(1, safeCount - 1);
  return Array.from({ length: safeCount }).flatMap((_, index) => {
    const y = top + step * index;
    return [
      { id: index * 2 + 1, x: left, y },
      { id: index * 2 + 2, x: right, y },
    ];
  });
};

const HorizontalZigzagTrainingVisual: React.FC<{
  bpm: number;
  durationSec: number;
  onOpenMetronome: (bpm: number) => void;
}> = ({ bpm, durationSec, onOpenMetronome }) => {
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const tickAudioRef = useRef<HTMLAudioElement | null>(null);
  const lastTickAtRef = useRef(Number.NEGATIVE_INFINITY);
  const nodes = useMemo(() => createTrainingHorizontalZigzagNodes(7), []);
  const traversalPath = useMemo(() => ([
    ...Array.from({ length: nodes.length }, (_, index) => index),
    ...Array.from({ length: Math.max(0, nodes.length - 2) }, (_, index) => nodes.length - 2 - index),
  ]), [nodes.length]);
  const activeNode = nodes[traversalPath[activeIndex] || 0] || nodes[0];
  const intervalMs = Math.max(250, Math.round(60000 / Math.max(40, bpm)));

  const playTick = useCallback(() => {
    if (muted || typeof window === 'undefined') return;
    const now = window.performance.now();
    if (now - lastTickAtRef.current < intervalMs - 8) return;
    lastTickAtRef.current = now;
    playLessonMetronomeTick(tickAudioRef, intervalMs);
  }, [intervalMs, muted]);

  useEffect(() => {
    setRunning(false);
    setElapsedSeconds(0);
    setActiveIndex(0);
  }, [bpm, durationSec]);

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      playTick();
      setActiveIndex((prev) => (prev + 1) % traversalPath.length);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs, playTick, running, traversalPath.length]);

  useEffect(() => () => {
    if (tickAudioRef.current) {
      tickAudioRef.current.pause();
      tickAudioRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = prev + 1;
        if (next >= durationSec) setRunning(false);
        return Math.min(next, durationSec);
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [durationSec, running]);

  const progress = durationSec ? Math.min(100, (elapsedSeconds / durationSec) * 100) : 0;
  const remainingSec = Math.max(0, durationSec - elapsedSeconds);

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-2xl border border-[#dbe7ec] bg-white p-3">
      <div className="flex shrink-0 items-start justify-between gap-3">
        <div>
          <p className="text-sm font-extrabold text-[#174e67]">Yatay Zikzak</p>
          <p className="mt-1 text-xs font-semibold leading-5 text-[#67808c]">
            Başını sabit tut; her tıkta sıradaki noktaya yalnızca gözlerinle geç.
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-[#eef6f8] px-3 py-1 text-[11px] font-black text-[#174e67]">
          {bpm} BPM
        </span>
      </div>

      <div className="mt-3 flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-2">
        <svg viewBox="0 0 1000 1000" className="block h-full max-h-full w-auto max-w-full">
          {nodes.slice(0, -1).map((node, index) => {
            const to = nodes[index + 1];
            return (
              <line
                key={`${node.id}_${to.id}`}
                x1={node.x}
                y1={node.y}
                x2={to.x}
                y2={to.y}
                stroke="#d6dee3"
                strokeWidth={4}
                opacity={0.9}
              />
            );
          })}
          {nodes.map((node) => {
            const isActive = node.id === activeNode.id;
            return (
              <g key={node.id}>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={isActive ? 28 : 17}
                  fill={isActive ? '#0f7f96' : '#d6dce0'}
                />
                {isActive ? (
                  <circle cx={node.x} cy={node.y} r={44} fill="none" stroke="#0ea5b7" strokeWidth={4} opacity={0.28} />
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="mt-2 shrink-0">
        <div className="h-2 overflow-hidden rounded-full bg-[#e5eef2]">
          <div className="h-full rounded-full bg-[#0ea5b7]" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs font-bold text-[#67808c]">
          <span>{Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}</span>
          <span>{Math.floor(remainingSec / 60)}:{String(remainingSec % 60).padStart(2, '0')} kaldı</span>
        </div>
      </div>

      <div className="mt-2 grid shrink-0 grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => setRunning((prev) => !prev)}
          className="rounded-2xl bg-[#0f7f96] px-4 py-2.5 text-sm font-black text-white shadow-[0_12px_30px_rgba(15,127,150,0.22)] transition active:scale-95"
        >
          {running ? 'Duraklat' : elapsedSeconds >= durationSec ? 'Yeniden başlat' : 'Başlat'}
        </button>
        <button
          type="button"
          onClick={() => {
            setRunning(false);
            setElapsedSeconds(0);
            setActiveIndex(0);
          }}
          className="rounded-2xl bg-[#eef6f8] px-4 py-2.5 text-sm font-black text-[#174e67] transition hover:bg-[#dff0f3]"
        >
          Sıfırla
        </button>
        <button
          type="button"
          onClick={() => setMuted((prev) => !prev)}
          className="rounded-2xl bg-[#eef6f8] px-3 py-2.5 text-sm font-black text-[#174e67] transition hover:bg-[#dff0f3]"
        >
          Ses {muted ? 'kapalı' : 'açık'}
        </button>
      </div>
    </div>
  );
};

const createThreeColumnGridNodes = () => {
  const cols = [200, 500, 800];
  const rows = [200, 500, 800];
  return rows.flatMap((y, rowIndex) =>
    cols.map((x, colIndex) => ({ id: rowIndex * 3 + colIndex + 1, x, y }))
  );
};

const ThreeColumnGridTrainingVisual: React.FC<{
  bpm: number;
  durationSec: number;
  onOpenMetronome: (bpm: number) => void;
}> = ({ bpm, durationSec, onOpenMetronome }) => {
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const tickAudioRef = useRef<HTMLAudioElement | null>(null);
  const lastTickAtRef = useRef(Number.NEGATIVE_INFINITY);
  const directionRef = useRef<1 | -1>(1);
  const nodes = useMemo(() => createThreeColumnGridNodes(), []);
  const intervalMs = Math.max(250, Math.round(60000 / Math.max(40, bpm)));

  const playTick = useCallback(() => {
    if (muted || typeof window === 'undefined') return;
    const now = window.performance.now();
    if (now - lastTickAtRef.current < intervalMs - 8) return;
    lastTickAtRef.current = now;
    playLessonMetronomeTick(tickAudioRef, intervalMs);
  }, [intervalMs, muted]);

  useEffect(() => {
    setRunning(false);
    setElapsedSeconds(0);
    setActiveIndex(0);
    directionRef.current = 1;
  }, [bpm, durationSec]);

  useEffect(() => {
    if (!running) return undefined;
    lastTickAtRef.current = Number.NEGATIVE_INFINITY;
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => {
        if (prev >= nodes.length - 1) directionRef.current = -1;
        else if (prev <= 0) directionRef.current = 1;
        const next = prev + directionRef.current;
        return Math.max(0, Math.min(nodes.length - 1, next));
      });
      playTick();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs, nodes.length, playTick, running]);

  useEffect(() => {
    if (running) return;
    if (tickAudioRef.current) {
      tickAudioRef.current.pause();
      tickAudioRef.current.currentTime = 0;
    }
  }, [running]);

  useEffect(() => () => {
    if (tickAudioRef.current) {
      tickAudioRef.current.pause();
      tickAudioRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = prev + 1;
        if (next >= durationSec) setRunning(false);
        return Math.min(next, durationSec);
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [durationSec, running]);

  const activeNode = nodes[activeIndex] || nodes[0];
  const progress = durationSec ? Math.min(100, (elapsedSeconds / durationSec) * 100) : 0;
  const remainingSec = Math.max(0, durationSec - elapsedSeconds);

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-2xl border border-[#dbe7ec] bg-white p-3">
      <div className="flex shrink-0 items-start justify-between gap-3">
        <div>
          <p className="text-sm font-extrabold text-[#174e67]">3 Sütun Grid</p>
          <p className="mt-1 text-xs font-semibold leading-5 text-[#67808c]">
            Başını sabit tut; her tıkta sıradaki noktaya yalnızca gözlerinle geç.
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-[#eef6f8] px-3 py-1 text-[11px] font-black text-[#174e67]">
          {bpm} BPM
        </span>
      </div>

      <div className="mt-3 flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-2">
        <svg viewBox="0 0 1000 1000" className="block h-full max-h-full w-auto max-w-full">
          {nodes.map((node) => {
            const isActive = node.id === activeNode.id;
            return (
              <g key={node.id}>
                <circle cx={node.x} cy={node.y} r={isActive ? 70 : 50} fill={isActive ? '#0f7f96' : '#d6dce0'} />
                {isActive ? (
                  <circle cx={node.x} cy={node.y} r={88} fill="none" stroke="#0ea5b7" strokeWidth={4} opacity={0.28} />
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="mt-2 shrink-0">
        <div className="h-2 overflow-hidden rounded-full bg-[#e5eef2]">
          <div className="h-full rounded-full bg-[#0ea5b7]" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs font-bold text-[#67808c]">
          <span>{Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}</span>
          <span>{Math.floor(remainingSec / 60)}:{String(remainingSec % 60).padStart(2, '0')} kaldı</span>
        </div>
      </div>

      <div className="mt-2 grid shrink-0 grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => setRunning((prev) => !prev)}
          className="rounded-2xl bg-[#0f7f96] px-4 py-2.5 text-sm font-black text-white shadow-[0_12px_30px_rgba(15,127,150,0.22)] transition active:scale-95"
        >
          {running ? 'Duraklat' : elapsedSeconds >= durationSec ? 'Yeniden başlat' : 'Başlat'}
        </button>
        <button
          type="button"
          onClick={() => {
            setRunning(false);
            setElapsedSeconds(0);
            setActiveIndex(0);
          }}
          className="rounded-2xl bg-[#eef6f8] px-4 py-2.5 text-sm font-black text-[#174e67] transition hover:bg-[#dff0f3]"
        >
          Sıfırla
        </button>
        <button
          type="button"
          onClick={() => setMuted((prev) => !prev)}
          className="rounded-2xl bg-[#eef6f8] px-3 py-2.5 text-sm font-black text-[#174e67] transition hover:bg-[#dff0f3]"
        >
          Ses {muted ? 'kapalı' : 'açık'}
        </button>
      </div>
    </div>
  );
};

const getBalloonReadingRows = (items: BalloonReadingItem[]) => {
  const rows = new Map<number, BalloonReadingItem[]>();
  items.forEach((item) => {
    const rowIndex = Math.max(1, Number(item.rowIndex) || 1);
    rows.set(rowIndex, [...(rows.get(rowIndex) || []), item]);
  });
  return [...rows.entries()]
    .sort(([a], [b]) => a - b)
    .map(([rowIndex, rowItems]) => {
      const sorted = [...rowItems].sort((a, b) => a.centerXPercent - b.centerXPercent);
      const left = Math.min(...sorted.map((item) => item.centerXPercent - item.widthPercent / 2));
      const right = Math.max(...sorted.map((item) => item.centerXPercent + item.widthPercent / 2));
      const top = Math.min(...sorted.map((item) => item.centerYPercent - item.heightPercent / 2));
      const bottom = Math.max(...sorted.map((item) => item.centerYPercent + item.heightPercent / 2));
      return {
        rowIndex,
        items: sorted,
        centerXPercent: (left + right) / 2,
        centerYPercent: (top + bottom) / 2,
        widthPercent: Math.max(1, right - left),
        heightPercent: Math.max(1, bottom - top),
      };
    });
};

const BalloonReadingTrainingVisual: React.FC<{
  material: LessonVisualMaterial;
  checkpoint: LessonVisualCheckpoint | null;
}> = ({ material, checkpoint }) => {
  const mode = String(checkpoint?.checkpointRole || '').includes('line') ? 'line' : 'bubble';
  const bpm = extractLessonMetronomeBpm(checkpoint, material, mode === 'line' ? 60 : 120);
  const durationSec = Math.max(30, Math.min(600, Number(material.balloonReadingDurationSec) || 180));
  const intervalMs = Math.max(250, Math.round(60000 / Math.max(40, bpm)));
  const items = useMemo(() => (
    (Array.isArray(material.balloonReadingItems) ? material.balloonReadingItems : [])
      .filter((item) => (
        Number.isFinite(item.centerXPercent) &&
        Number.isFinite(item.centerYPercent) &&
        Number.isFinite(item.widthPercent) &&
        Number.isFinite(item.heightPercent)
      ))
      .sort((a, b) => a.index - b.index)
  ), [material.balloonReadingItems]);
  const rows = useMemo(() => getBalloonReadingRows(items), [items]);
  const targets = mode === 'line' ? rows : items;
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const tickAudioRef = useRef<HTMLAudioElement | null>(null);
  const lastTickAtRef = useRef(Number.NEGATIVE_INFINITY);

  const playTick = useCallback(() => {
    if (muted || typeof window === 'undefined') return;
    const now = window.performance.now();
    if (now - lastTickAtRef.current < intervalMs - 8) return;
    lastTickAtRef.current = now;
    playLessonMetronomeTick(tickAudioRef, intervalMs);
  }, [intervalMs, muted]);

  useEffect(() => {
    setRunning(false);
    setElapsedSeconds(0);
    setActiveIndex(0);
  }, [bpm, durationSec, mode, targets.length]);

  useEffect(() => {
    if (!running || !targets.length) return undefined;
    lastTickAtRef.current = Number.NEGATIVE_INFINITY;
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % targets.length);
      playTick();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs, playTick, running, targets.length]);

  useEffect(() => {
    if (running) return;
    if (tickAudioRef.current) {
      tickAudioRef.current.pause();
      tickAudioRef.current.currentTime = 0;
    }
  }, [running]);

  useEffect(() => () => {
    if (tickAudioRef.current) {
      tickAudioRef.current.pause();
      tickAudioRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = prev + 1;
        if (next >= durationSec) setRunning(false);
        return Math.min(next, durationSec);
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [durationSec, running]);

  const activeTarget = targets[activeIndex] || targets[0];
  const progress = durationSec ? Math.min(100, (elapsedSeconds / durationSec) * 100) : 0;
  const remainingSec = Math.max(0, durationSec - elapsedSeconds);

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-2xl border border-[#dbe7ec] bg-white p-3">
      <div className="flex shrink-0 items-start justify-between gap-3">
        <div>
          <p className="text-sm font-extrabold text-[#174e67]">{mode === 'line' ? 'Satır Okuma' : 'Balon Okuma'}</p>
          <p className="mt-1 text-xs font-semibold leading-5 text-[#67808c]">
            {mode === 'line' ? 'Her tıkta vurgulanan satır boyunca gözlerinle ilerle.' : 'Her tıkta vurgulanan öbeği tek bakışta gör.'}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-[#eef6f8] px-3 py-1 text-[11px] font-black text-[#174e67]">
          {bpm} BPM
        </span>
      </div>

      <div className="mt-3 flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-2">
        <div className="relative inline-block h-full max-h-full max-w-full overflow-hidden rounded-xl bg-white shadow-[0_8px_28px_rgba(23,78,103,0.14)]">
          {material.media?.url ? (
            <img src={material.media.url} alt={material.title} className="block h-full w-auto max-w-full select-none object-contain" draggable={false} />
          ) : null}
          {activeTarget ? (
            <div
              className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-1/2 rounded-lg border-2 border-[#0ea5b7] bg-[#0ea5b7]/18 shadow-[0_0_0_3px_rgba(255,255,255,0.78),0_10px_28px_rgba(14,165,183,0.28)] transition-[left,top,width,height] duration-200 ease-out"
              style={{
                left: `${activeTarget.centerXPercent}%`,
                top: `${activeTarget.centerYPercent}%`,
                width: `${activeTarget.widthPercent}%`,
                height: `${activeTarget.heightPercent}%`,
              }}
            />
          ) : null}
        </div>
      </div>

      {!targets.length ? (
        <p className="mt-3 rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm font-semibold text-[#a04c4c]">
          Bu Balon Okuma görselinden takip edilecek öbekler çıkarılamadı.
        </p>
      ) : (
        <div className="mt-2 shrink-0 rounded-2xl bg-[#eef6f8] px-4 py-2">
          <div className="h-2 overflow-hidden rounded-full bg-[#dbe7ec]">
            <div className="h-full rounded-full bg-[#0ea5b7]" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs font-bold text-[#67808c]">
            <span>{activeIndex + 1}/{targets.length}</span>
            <span>{Math.floor(remainingSec / 60)}:{String(remainingSec % 60).padStart(2, '0')} kaldı</span>
          </div>
        </div>
      )}

      <div className="mt-2 grid shrink-0 grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => setRunning((prev) => !prev)}
          disabled={!targets.length}
          className="rounded-2xl bg-[#0f7f96] px-4 py-2.5 text-sm font-black text-white shadow-[0_12px_30px_rgba(15,127,150,0.22)] transition active:scale-95 disabled:cursor-not-allowed disabled:bg-[#bfd1d8]"
        >
          {running ? 'Duraklat' : elapsedSeconds >= durationSec ? 'Yeniden başlat' : 'Başlat'}
        </button>
        <button
          type="button"
          onClick={() => {
            setRunning(false);
            setElapsedSeconds(0);
            setActiveIndex(0);
          }}
          className="rounded-2xl bg-[#eef6f8] px-4 py-2.5 text-sm font-black text-[#174e67] transition hover:bg-[#dff0f3]"
        >
          Sıfırla
        </button>
        <button
          type="button"
          onClick={() => setMuted((prev) => !prev)}
          className="rounded-2xl bg-[#eef6f8] px-3 py-2.5 text-sm font-black text-[#174e67] transition hover:bg-[#dff0f3]"
        >
          Ses {muted ? 'kapalı' : 'açık'}
        </button>
      </div>
    </div>
  );
};

type StarReadingImageCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
};

const getFullStarReadingImageCrop = (image: HTMLImageElement): StarReadingImageCrop => {
  const naturalWidth = Math.max(1, image.naturalWidth || image.width || 1);
  const naturalHeight = Math.max(1, image.naturalHeight || image.height || 1);
  return {
    x: 0,
    y: 0,
    width: naturalWidth,
    height: naturalHeight,
    naturalWidth,
    naturalHeight,
  };
};

const getFallbackStarReadingImageCrop = (image: HTMLImageElement): StarReadingImageCrop => {
  const fullCrop = getFullStarReadingImageCrop(image);
  const aspectRatio = fullCrop.naturalWidth / fullCrop.naturalHeight;

  if (aspectRatio <= 1.15) return fullCrop;

  const widthRatio = aspectRatio >= 2 ? 0.48 : 0.62;
  const heightRatio = 0.96;
  const width = Math.round(fullCrop.naturalWidth * widthRatio);
  const height = Math.round(fullCrop.naturalHeight * heightRatio);

  return {
    x: Math.max(0, Math.round((fullCrop.naturalWidth - width) / 2)),
    y: Math.max(0, Math.round((fullCrop.naturalHeight - height) / 2)),
    width: Math.min(fullCrop.naturalWidth, width),
    height: Math.min(fullCrop.naturalHeight, height),
    naturalWidth: fullCrop.naturalWidth,
    naturalHeight: fullCrop.naturalHeight,
  };
};

const detectStarReadingImageCrop = (image: HTMLImageElement): StarReadingImageCrop => {
  const fullCrop = getFullStarReadingImageCrop(image);
  if (typeof document === 'undefined') return fullCrop;

  const maxSide = 900;
  const scale = Math.min(1, maxSide / Math.max(fullCrop.naturalWidth, fullCrop.naturalHeight));
  const canvasWidth = Math.max(1, Math.round(fullCrop.naturalWidth * scale));
  const canvasHeight = Math.max(1, Math.round(fullCrop.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return fullCrop;

  context.drawImage(image, 0, 0, canvasWidth, canvasHeight);
  const { data } = context.getImageData(0, 0, canvasWidth, canvasHeight);

  let minX = canvasWidth;
  let minY = canvasHeight;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < canvasHeight; y += 1) {
    for (let x = 0; x < canvasWidth; x += 1) {
      const offset = (y * canvasWidth + x) * 4;
      const alpha = data[offset + 3];
      if (alpha < 24) continue;

      const r = data[offset];
      const g = data[offset + 1];
      const b = data[offset + 2];
      const maxChannel = Math.max(r, g, b);
      const minChannel = Math.min(r, g, b);
      const darkness = 255 - (r + g + b) / 3;
      const chroma = maxChannel - minChannel;

      if (darkness < 52 && !(chroma > 34 && darkness > 24)) continue;

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (maxX < minX || maxY < minY) return getFallbackStarReadingImageCrop(image);

  const paddingX = Math.max(10, Math.round(canvasWidth * 0.045));
  const paddingY = Math.max(10, Math.round(canvasHeight * 0.045));
  minX = Math.max(0, minX - paddingX);
  minY = Math.max(0, minY - paddingY);
  maxX = Math.min(canvasWidth - 1, maxX + paddingX);
  maxY = Math.min(canvasHeight - 1, maxY + paddingY);

  const inverseScale = 1 / scale;
  const crop = {
    x: Math.max(0, Math.floor(minX * inverseScale)),
    y: Math.max(0, Math.floor(minY * inverseScale)),
    width: Math.min(fullCrop.naturalWidth, Math.ceil((maxX - minX + 1) * inverseScale)),
    height: Math.min(fullCrop.naturalHeight, Math.ceil((maxY - minY + 1) * inverseScale)),
    naturalWidth: fullCrop.naturalWidth,
    naturalHeight: fullCrop.naturalHeight,
  };

  const retainedWidthRatio = crop.width / fullCrop.naturalWidth;
  const retainedHeightRatio = crop.height / fullCrop.naturalHeight;
  if (fullCrop.naturalWidth / fullCrop.naturalHeight > 1.15 && retainedWidthRatio > 0.82 && retainedHeightRatio > 0.82) {
    return getFallbackStarReadingImageCrop(image);
  }

  return crop;
};

const StarReadingTrainingVisual: React.FC<{
  material: LessonVisualMaterial;
  checkpoint: LessonVisualCheckpoint | null;
}> = ({ material, checkpoint }) => {
  const bpm = extractLessonMetronomeBpm(checkpoint, material, 120);
  const durationSec = Math.max(30, Math.min(600, Number(material.starReadingDurationSec) || 120));
  const intervalMs = Math.max(250, Math.round(60000 / Math.max(40, bpm)));
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [imageCrop, setImageCrop] = useState<StarReadingImageCrop | null>(null);
  const tickAudioRef = useRef<HTMLAudioElement | null>(null);
  const lastTickAtRef = useRef(Number.NEGATIVE_INFINITY);

  const playTick = useCallback(() => {
    if (muted || typeof window === 'undefined') return;
    const now = window.performance.now();
    if (now - lastTickAtRef.current < intervalMs - 8) return;
    lastTickAtRef.current = now;
    playLessonMetronomeTick(tickAudioRef, intervalMs);
  }, [intervalMs, muted]);

  useEffect(() => {
    setRunning(false);
    setElapsedSeconds(0);
  }, [bpm, durationSec, material.id]);

  useEffect(() => {
    const imageUrl = material.media?.url || '';
    if (!imageUrl) {
      setImageCrop(null);
      return undefined;
    }

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (cancelled) return;
      try {
        setImageCrop(detectStarReadingImageCrop(image));
      } catch {
        setImageCrop(getFallbackStarReadingImageCrop(image));
      }
    };
    image.onerror = () => {
      const fallbackImage = new Image();
      fallbackImage.onload = () => {
        if (!cancelled) setImageCrop(getFallbackStarReadingImageCrop(fallbackImage));
      };
      fallbackImage.onerror = () => {
        if (!cancelled) setImageCrop(null);
      };
      fallbackImage.src = imageUrl;
    };
    image.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [material.media?.url]);

  useEffect(() => {
    if (!running) return undefined;
    lastTickAtRef.current = Number.NEGATIVE_INFINITY;
    const timer = window.setInterval(() => {
      playTick();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs, playTick, running]);

  useEffect(() => {
    if (running) return;
    if (tickAudioRef.current) {
      tickAudioRef.current.pause();
      tickAudioRef.current.currentTime = 0;
    }
  }, [running]);

  useEffect(() => () => {
    if (tickAudioRef.current) {
      tickAudioRef.current.pause();
      tickAudioRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = prev + 1;
        if (next >= durationSec) setRunning(false);
        return Math.min(next, durationSec);
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [durationSec, running]);

  const progress = durationSec ? Math.min(100, (elapsedSeconds / durationSec) * 100) : 0;
  const remainingSec = Math.max(0, durationSec - elapsedSeconds);

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-2xl border border-[#dbe7ec] bg-white p-3">
      <div className="flex shrink-0 items-start justify-between gap-3">
        <div>
          <p className="text-sm font-extrabold text-[#174e67]">Yıldız Okuma</p>
          <p className="mt-1 text-xs font-semibold leading-5 text-[#67808c]">
            Her tıkta yıldızları takip ederek ikili kelime öbeklerini gör.
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-[#eef6f8] px-3 py-1 text-[11px] font-black text-[#174e67]">
          {bpm} BPM
        </span>
      </div>

      <div className="mt-3 flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-2">
        {material.media?.url && imageCrop ? (
          <svg
            viewBox={`${imageCrop.x} ${imageCrop.y} ${imageCrop.width} ${imageCrop.height}`}
            preserveAspectRatio="xMidYMid meet"
            className="block h-full w-full select-none rounded-xl bg-white shadow-[0_8px_28px_rgba(23,78,103,0.14)]"
            role="img"
            aria-label={material.title}
          >
            <image
              href={material.media.url}
              x={0}
              y={0}
              width={imageCrop.naturalWidth}
              height={imageCrop.naturalHeight}
              preserveAspectRatio="none"
            />
          </svg>
        ) : material.media?.url ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm font-semibold text-[#67808c]">Görsel hazırlanıyor...</p>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm font-semibold text-[#67808c]">Yıldız Okuma görseli bulunamadı.</p>
          </div>
        )}
      </div>

      <div className="mt-2 shrink-0 rounded-2xl bg-[#eef6f8] px-4 py-2">
        <div className="h-2 overflow-hidden rounded-full bg-[#dbe7ec]">
          <div className="h-full rounded-full bg-[#0ea5b7]" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs font-bold text-[#67808c]">
          <span>{Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}</span>
          <span>{Math.floor(remainingSec / 60)}:{String(remainingSec % 60).padStart(2, '0')} kaldı</span>
        </div>
      </div>

      <div className="mt-2 grid shrink-0 grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => setRunning((prev) => !prev)}
          disabled={!material.media?.url}
          className="rounded-2xl bg-[#0f7f96] px-4 py-2.5 text-sm font-black text-white shadow-[0_12px_30px_rgba(15,127,150,0.22)] transition active:scale-95 disabled:cursor-not-allowed disabled:bg-[#bfd1d8]"
        >
          {running ? 'Duraklat' : elapsedSeconds >= durationSec ? 'Yeniden başlat' : 'Başlat'}
        </button>
        <button
          type="button"
          onClick={() => {
            setRunning(false);
            setElapsedSeconds(0);
          }}
          className="rounded-2xl bg-[#eef6f8] px-4 py-2.5 text-sm font-black text-[#174e67] transition hover:bg-[#dff0f3]"
        >
          Sıfırla
        </button>
        <button
          type="button"
          onClick={() => setMuted((prev) => !prev)}
          className="rounded-2xl bg-[#eef6f8] px-3 py-2.5 text-sm font-black text-[#174e67] transition hover:bg-[#dff0f3]"
        >
          Ses {muted ? 'kapalı' : 'açık'}
        </button>
      </div>
    </div>
  );
};

const GapReadingTrainingVisual: React.FC<{
  material: LessonVisualMaterial;
  checkpoint: LessonVisualCheckpoint | null;
}> = ({ material, checkpoint }) => {
  const bpm = extractLessonMetronomeBpm(checkpoint, material, 120);
  const durationSec = Math.max(30, Math.min(600, Number(material.gapReadingDurationSec) || 120));
  const intervalMs = Math.max(250, Math.round(60000 / Math.max(40, bpm)));
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const tickAudioRef = useRef<HTMLAudioElement | null>(null);
  const lastTickAtRef = useRef(Number.NEGATIVE_INFINITY);

  useEffect(() => {
    if (running) pauseSpeedReadingMetronome();
  }, [running]);

  const playTick = useCallback(() => {
    if (muted || typeof window === 'undefined') return;
    const now = window.performance.now();
    if (now - lastTickAtRef.current < intervalMs - 8) return;
    lastTickAtRef.current = now;
    playLessonMetronomeTick(tickAudioRef, intervalMs);
  }, [intervalMs, muted]);

  useEffect(() => {
    setRunning(false);
    setElapsedSeconds(0);
  }, [bpm, durationSec, material.id]);

  useEffect(() => {
    if (!running) return undefined;
    lastTickAtRef.current = Number.NEGATIVE_INFINITY;
    const timer = window.setInterval(() => {
      playTick();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs, playTick, running]);

  useEffect(() => {
    if (running) return;
    if (tickAudioRef.current) {
      tickAudioRef.current.pause();
      tickAudioRef.current.currentTime = 0;
    }
  }, [running]);

  useEffect(() => () => {
    if (tickAudioRef.current) {
      tickAudioRef.current.pause();
      tickAudioRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = prev + 1;
        if (next >= durationSec) setRunning(false);
        return Math.min(next, durationSec);
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [durationSec, running]);

  const progress = durationSec ? Math.min(100, (elapsedSeconds / durationSec) * 100) : 0;
  const remainingSec = Math.max(0, durationSec - elapsedSeconds);

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-2xl border border-[#dbe7ec] bg-white p-3">
      <div className="flex shrink-0 items-start justify-between gap-3">
        <div>
          <p className="text-sm font-extrabold text-[#174e67]">Boşluk Okuma</p>
        </div>
        <span className="shrink-0 rounded-full bg-[#eef6f8] px-3 py-1 text-[11px] font-black text-[#174e67]">
          {bpm} BPM
        </span>
      </div>

      <div className="mt-3 flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-2">
        {material.media?.url ? (
          <img
            src={material.media.url}
            alt={material.title}
            className="block h-full w-full select-none rounded-xl bg-white object-contain shadow-[0_8px_28px_rgba(23,78,103,0.14)]"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm font-semibold text-[#67808c]">Boşluk Okuma görseli bulunamadı.</p>
          </div>
        )}
      </div>

      <div className="mt-2 shrink-0 rounded-2xl bg-[#eef6f8] px-4 py-2">
        <div className="h-2 overflow-hidden rounded-full bg-[#dbe7ec]">
          <div className="h-full rounded-full bg-[#0ea5b7]" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs font-bold text-[#67808c]">
          <span>{Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}</span>
          <span>{Math.floor(remainingSec / 60)}:{String(remainingSec % 60).padStart(2, '0')} kaldı</span>
        </div>
      </div>

      <div className="mt-2 grid shrink-0 grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => {
            if (!running && elapsedSeconds >= durationSec) setElapsedSeconds(0);
            setRunning((prev) => !prev);
          }}
          disabled={!material.media?.url}
          className="rounded-2xl bg-[#0f7f96] px-4 py-2.5 text-sm font-black text-white shadow-[0_12px_30px_rgba(15,127,150,0.22)] transition active:scale-95 disabled:cursor-not-allowed disabled:bg-[#bfd1d8]"
        >
          {running ? 'Duraklat' : elapsedSeconds >= durationSec ? 'Yeniden başlat' : 'Başlat'}
        </button>
        <button
          type="button"
          onClick={() => {
            setRunning(false);
            setElapsedSeconds(0);
          }}
          className="rounded-2xl bg-[#eef6f8] px-4 py-2.5 text-sm font-black text-[#174e67] transition hover:bg-[#dff0f3]"
        >
          Sıfırla
        </button>
        <button
          type="button"
          onClick={() => setMuted((prev) => !prev)}
          className="rounded-2xl bg-[#eef6f8] px-3 py-2.5 text-sm font-black text-[#174e67] transition hover:bg-[#dff0f3]"
        >
          Ses {muted ? 'kapalı' : 'açık'}
        </button>
      </div>
    </div>
  );
};

const MeasurementTextTrainingVisual: React.FC<{
  readingMaterials: LessonVisualMaterial[];
  questions: VisualQuestionItem[];
  instruction: string;
  formatSeconds: (value: number) => string;
  onComplete: (summary: VisualSummaryState) => void;
}> = ({ readingMaterials, questions, instruction, formatSeconds, onComplete }) => {
  const [phase, setPhase] = useState<'ready' | 'countdown' | 'reading' | 'questions'>('ready');
  const [countdown, setCountdown] = useState(3);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number | undefined>>({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const totalStartedAtRef = useRef<number | null>(null);
  const pageStartedAtRef = useRef<number | null>(null);
  const pageTimesRef = useRef<number[]>([]);
  const activeMaterial = readingMaterials[Math.min(activePageIndex, Math.max(0, readingMaterials.length - 1))] || null;
  const activeQuestion = questions[Math.min(questionIndex, Math.max(0, questions.length - 1))] || null;
  const answeredQuestionCount = questions.filter((question) => answers[question.key] !== undefined).length;
  const allAnswered = questions.length === 0 || answeredQuestionCount === questions.length;

  const resetMeasurement = () => {
    setPhase('ready');
    setCountdown(3);
    setActivePageIndex(0);
    setElapsedSeconds(0);
    pageTimesRef.current = [];
    setAnswers({});
    setQuestionIndex(0);
    totalStartedAtRef.current = null;
    pageStartedAtRef.current = null;
  };

  const startCountdown = () => {
    setCountdown(3);
    setElapsedSeconds(0);
    pageTimesRef.current = [];
    setAnswers({});
    setQuestionIndex(0);
    setActivePageIndex(0);
    totalStartedAtRef.current = null;
    pageStartedAtRef.current = null;
    setPhase('countdown');
  };

  const startReading = () => {
    const now = Date.now();
    totalStartedAtRef.current = now;
    pageStartedAtRef.current = now;
    setElapsedSeconds(0);
    setPhase('reading');
  };

  const getCurrentPageSeconds = () => (
    pageStartedAtRef.current === null ? 0 : Math.max(1, Math.round((Date.now() - pageStartedAtRef.current) / 1000))
  );

  const finishCurrentPage = () => {
    if (!activeMaterial) return;
    const currentPageSeconds = getCurrentPageSeconds();
    const nextPageTimes = [...pageTimesRef.current];
    nextPageTimes[activePageIndex] = currentPageSeconds;
    pageTimesRef.current = nextPageTimes;

    if (activePageIndex < readingMaterials.length - 1) {
      setActivePageIndex((prev) => prev + 1);
      pageStartedAtRef.current = Date.now();
      return;
    }

    pageStartedAtRef.current = null;
    setPhase('questions');
  };

  const completeQuestions = () => {
    if (!allAnswered) return;
    onComplete({
      title: 'Ölçüm metni sonucu',
      pageTimes: readingMaterials.map((material, index) => ({
        title: material.title,
        pageLabel: material.pageLabel,
        seconds: pageTimesRef.current[index] || 0,
      })),
      questions: questions.map((question) => ({
        ...question,
        selectedOptionIndex: answers[question.key] ?? null,
      })),
      memoryPegs: [],
      wordAlternatives: [],
      numberTracking: [],
    });
  };

  useEffect(() => {
    if (phase !== 'countdown') return undefined;
    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          window.setTimeout(startReading, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'reading') return undefined;
    const timer = window.setInterval(() => {
      const startedAt = totalStartedAtRef.current;
      setElapsedSeconds(startedAt === null ? 0 : Math.max(0, Math.round((Date.now() - startedAt) / 1000)));
    }, 250);
    return () => window.clearInterval(timer);
  }, [phase]);

  if (!readingMaterials.length) {
    return (
      <div className="mt-4 rounded-2xl border border-[#dbe7ec] bg-white p-4">
        <p className="rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm font-semibold text-[#a04c4c]">
          Ölçüm metni için okuma sayfası bulunamadı.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-2xl border border-[#dbe7ec] bg-white p-3">
      {phase === 'ready' ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-5 text-center">
          <p className="text-sm font-black text-[#174e67]">Ölçüm hazır</p>
          <p className="mt-2 text-sm font-semibold leading-6 text-[#67808c]">
            Başlatınca 3 saniyelik geri sayım yapılır. İlk sayfa açıldığında kronometre otomatik başlar.
          </p>
          <button
            type="button"
            onClick={startCountdown}
            className="mt-5 w-full rounded-2xl bg-[#0f7f96] px-4 py-3 text-sm font-black text-white shadow-[0_12px_30px_rgba(15,127,150,0.22)] transition active:scale-95"
          >
            Başlat
          </button>
        </div>
      ) : null}

      {phase === 'countdown' ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc]">
          <div className="text-center">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#0ea5b7]">Okuma başlıyor</p>
            <p className="mt-3 text-7xl font-black leading-none text-[#174e67]">{countdown}</p>
          </div>
        </div>
      ) : null}

      {phase === 'reading' && activeMaterial ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-3 rounded-2xl bg-[#f7fbfc] px-4 py-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-[#67808c]">Kronometre</p>
              <p className="mt-1 text-3xl font-black text-[#174e67]">{formatSeconds(elapsedSeconds)}</p>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-[#174e67] shadow-sm">
              {activePageIndex + 1}/{readingMaterials.length}
            </span>
          </div>

          <div className="mt-2 flex shrink-0 items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-[#174e67]">{activeMaterial.title}</p>
              <p className="mt-0.5 truncate text-xs font-semibold text-[#67808c]">{activeMaterial.pageLabel}</p>
            </div>
            <div className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#eef6f8] p-1 text-[#174e67]">
              <button type="button" onClick={() => setZoom((prev) => Math.max(0.8, Number((prev - 0.15).toFixed(2))))} className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-black shadow-sm">−</button>
              <span className="w-12 text-center text-[11px] font-black">{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={() => setZoom((prev) => Math.min(2.4, Number((prev + 0.15).toFixed(2))))} className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-black shadow-sm">+</button>
            </div>
          </div>

          <div className="mt-2 min-h-0 flex-1 overflow-auto rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-2">
            <img
              src={activeMaterial.media?.url}
              alt={activeMaterial.title}
              className="block shadow-[0_8px_28px_rgba(23,78,103,0.14)]"
              style={
                zoom === 1
                  ? { width: '100%', height: 'auto', display: 'block' }
                  : { width: `${zoom * 100}%`, height: 'auto', maxWidth: 'none', display: 'block' }
              }
            />
          </div>

          <button
            type="button"
            onClick={finishCurrentPage}
            className="mt-3 shrink-0 rounded-2xl bg-[#174e67] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#0d3a4f]"
          >
            {activePageIndex < readingMaterials.length - 1 ? `${activePageIndex + 1}. sayfayı bitirdim` : 'Okumayı bitirdim'}
          </button>
        </div>
      ) : null}

      {phase === 'questions' ? (
        <div className="mt-4">
          <div className="rounded-2xl border border-[#dbe7ec] bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-extrabold text-[#174e67]">Anlama soruları</p>
                <p className="mt-0.5 text-xs font-semibold text-[#67808c]">
                  Okuma süren kaydedildi. Soruları cevaplayınca sonuç ekranı açılır.
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-[#eef6f8] px-3 py-1 text-[11px] font-black text-[#174e67]">
                {questions.length ? `${Math.min(questionIndex + 1, questions.length)}/${questions.length}` : '0/0'}
              </span>
            </div>

            {activeQuestion ? (
              <>
                <p className="mt-4 text-base font-black leading-6 text-[#174e67]">{activeQuestion.question}</p>
                <div className="mt-4 grid gap-2">
                  {activeQuestion.options.map((option, optionIndex) => {
                    const isSelected = answers[activeQuestion.key] === optionIndex;
                    return (
                      <button
                        key={`${activeQuestion.key}_${optionIndex}`}
                        type="button"
                        onClick={() => setAnswers((prev) => ({ ...prev, [activeQuestion.key]: optionIndex }))}
                        className={`rounded-2xl border px-3 py-3 text-left text-sm font-semibold leading-5 transition ${isSelected
                            ? 'border-[#0ea5b7] bg-[#e9f6f8] text-[#174e67]'
                            : 'border-[#dbe7ec] bg-white text-[#1d4758] hover:border-[#0ea5b7] hover:bg-[#f7fbfc]'
                          }`}
                      >
                        <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#eef6f8] text-xs font-black text-[#174e67]">
                          {String.fromCharCode(65 + optionIndex)}
                        </span>
                        {option}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setQuestionIndex((prev) => Math.min(questions.length - 1, prev + 1))}
                    disabled={questionIndex >= questions.length - 1 || answers[activeQuestion.key] === undefined}
                    className="rounded-2xl bg-[#174e67] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#0d3a4f] disabled:cursor-not-allowed disabled:bg-[#bfd1d8]"
                  >
                    Sonraki soru
                  </button>
                </div>
              </>
            ) : (
              <p className="mt-4 rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm font-semibold text-[#a04c4c]">
                Soru görselinden çözülebilir soru çıkarılamadı.
              </p>
            )}
          </div>

          <div className="mt-3 rounded-2xl bg-[#eef6f8] px-4 py-3 text-xs font-bold text-[#67808c]">
            {answeredQuestionCount}/{questions.length} soru cevaplandı
          </div>
          <button
            type="button"
            onClick={completeQuestions}
            disabled={!allAnswered}
            className="mt-3 w-full rounded-2xl bg-[#174e67] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#0d3a4f] disabled:cursor-not-allowed disabled:bg-[#bfd1d8]"
          >
            Sonuçları göster
          </button>
          <button
            type="button"
            onClick={resetMeasurement}
            className="mt-2 w-full rounded-2xl bg-[#eef6f8] px-5 py-3 text-sm font-bold text-[#174e67] transition hover:bg-[#dff0f3]"
          >
            Baştan başlat
          </button>
        </div>
      ) : null}
    </div>
  );
};

const LessonPlayer: React.FC<LessonPlayerProps> = ({ lesson, onBack }) => {
  const navigate = useNavigate();
  const initialSnapshotRef = useRef<LessonPlayerSnapshot | null>(readLessonPlayerSnapshot(lesson.id));
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialSnapshotRef.current?.currentTime || 0);
  const [duration, setDuration] = useState(lesson.audioDurationMs ? lesson.audioDurationMs / 1000 : 0);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(initialSnapshotRef.current?.activeSegmentIndex || 0);
  const [mainAudioError, setMainAudioError] = useState<string | null>(null);
  const [rate, setRate] = useState(initialSnapshotRef.current?.rate || 1);
  const [showQuestion, setShowQuestion] = useState(false);
  const [pausedForQuestion, setPausedForQuestion] = useState(false);
  const [activeInteraction, setActiveInteraction] = useState<LessonInteractionQuiz | null>(null);
  const [activeVisualCheckpoint, setActiveVisualCheckpoint] = useState<LessonVisualCheckpoint | null>(null);
  const [visualPageIndex, setVisualPageIndex] = useState(0);
  const [maxVisualPageSeen, setMaxVisualPageSeen] = useState(0);
  const [visualZoom, setVisualZoom] = useState(1);
  const [visualPageTimes, setVisualPageTimes] = useState<Record<string, number>>({});
  const [visualAnswers, setVisualAnswers] = useState<Record<string, number>>({});
  const [visualMemoryPegAnswers, setVisualMemoryPegAnswers] = useState<Record<string, string>>({});
  const [visualWordAlternativeAnswers, setVisualWordAlternativeAnswers] = useState<Record<string, string>>({});
  const [visualWordAlternativeSelections, setVisualWordAlternativeSelections] = useState<Record<string, Array<number | null>>>({});
  const [visualQuestionIndex, setVisualQuestionIndex] = useState(0);
  const [visualSummary, setVisualSummary] = useState<VisualSummaryState | null>(null);
  const [numberTrackingPhase, setNumberTrackingPhase] = useState<1 | 2>(1);
  const [numberTrackingTimeLeft, setNumberTrackingTimeLeft] = useState(DEFAULT_NUMBER_TRACKING_DURATION_SEC);
  const [numberTrackingRunning, setNumberTrackingRunning] = useState(false);
  const [numberTrackingTransition, setNumberTrackingTransition] = useState(false);
  const [numberTrackingFinished, setNumberTrackingFinished] = useState(false);
  const [numberTrackingTargets, setNumberTrackingTargets] = useState<Record<1 | 2, number>>({ 1: 1, 2: 1 });
  const [numberTrackingSelections, setNumberTrackingSelections] = useState<Record<1 | 2, number[]>>({ 1: [], 2: [] });
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(null);
  const [interactionResult, setInteractionResult] = useState<InteractionAnswerResponse | null>(null);
  const [interactionLoading, setInteractionLoading] = useState(false);
  const [interactionError, setInteractionError] = useState<string | null>(null);
  const [isFeedbackPlaying, setIsFeedbackPlaying] = useState(false);
  const [feedbackAudioSrc, setFeedbackAudioSrc] = useState<string | null>(null);
  const [feedbackAudioError, setFeedbackAudioError] = useState<string | null>(null);
  const seenInteractionsRef = useRef<Set<string>>(new Set());
  const feedbackAudioRef = useRef<HTMLAudioElement | null>(null);
  const feedbackObjectUrlRef = useRef<string | null>(null);
  const visualPageStartedAtRef = useRef<number | null>(null);
  const visualPageTimesRef = useRef<Record<string, number>>({});
  const numberTrackingPhaseEndHandledRef = useRef(false);
  const numberTrackingTransitionTimeoutRef = useRef<number | null>(null);
  const openedVisualCheckpointSegmentRef = useRef<number | null>(null);
  const autoPlayAfterSegmentChangeRef = useRef(false);
  const pendingSegmentSeekRef = useRef<number | null>(null);
  const playbackWatchdogRef = useRef<number | null>(null);
  const mainAudioRepairingRef = useRef(false);
  const userPausedRef = useRef(false);
  const mainAudioElementsRef = useRef<Set<HTMLAudioElement>>(new Set());
  const restoredSnapshotRef = useRef(false);
  const latestSnapshotRef = useRef<LessonPlayerSnapshot | null>(initialSnapshotRef.current);

  const lessonSegments = useMemo<LessonAudioSegment[]>(() => {
    const rawSegments = Array.isArray(lesson.metadata?.lessonSegments) ? lesson.metadata.lessonSegments : [];
    return rawSegments
      .map((segment, index) => {
        const quiz = segment.quiz;
        const visualCheckpoint = segment.visualCheckpoint;
        const hasVisualCheckpoint = Boolean(visualCheckpoint?.visualMaterials?.length);
        const kind: LessonAudioSegment['kind'] = hasVisualCheckpoint
          ? 'visual'
          : segment.kind === 'quiz' || quiz
            ? 'quiz'
            : 'narration';
        return {
          index,
          kind,
          title: String(segment.title || quiz?.sectionTitle || visualCheckpoint?.title || `Bölüm ${index + 1}`).trim(),
          durationSec: Math.max(1, Math.round((Number(segment.durationMs) || 0) / 1000)),
          audioUrl: String(segment.audioMedia?.url || '').trim(),
          quiz: quiz ? {
            id: String(quiz?.id || `quiz_${index + 1}`),
            segmentIndex: index,
            timeSec: 0,
            sectionTitle: String(quiz?.sectionTitle || segment.title || '').trim(),
            question: String(quiz?.question || '').trim(),
            options: Array.isArray(quiz?.options) ? quiz.options.map((option) => String(option || '').trim()) : [],
            correctOptionIndex: Number(quiz?.correctOptionIndex),
            wrongFeedback: String(quiz?.wrongFeedback || '').trim(),
            feedbackAudioUrl: String(quiz?.feedbackAudioMedia?.url || '').trim(),
          } : undefined,
          visualCheckpoint: visualCheckpoint ? {
            id: String(visualCheckpoint.id || `visual_${index + 1}`),
            segmentIndex: index,
            title: String(visualCheckpoint.title || segment.title || '').trim(),
            checkpointRole: String(visualCheckpoint.checkpointRole || '').trim(),
            instruction: String(visualCheckpoint.instruction || '').trim(),
            requireLastPage: visualCheckpoint.requireLastPage !== false,
            visualMaterials: Array.isArray(visualCheckpoint.visualMaterials)
              ? visualCheckpoint.visualMaterials
                .map((material) => ({
                  id: material.id,
                  title: String(material.title || '').trim(),
                  pageLabel: String(material.pageLabel || '').trim(),
                  note: String(material.note || '').trim(),
                  visualCategory: material.visualCategory || (material.isQuestionVisual ? 'question' : 'reading'),
                  measurementTextRole: String(material.measurementTextRole || '').trim(),
                  isQuestionVisual: getLessonVisualCategory(material) === 'question',
                  trackTime: getLessonVisualCategory(material) === 'reading' ? material.trackTime !== false : false,
                  questionCount: material.questionCount || 0,
                  optionCount: material.optionCount || 0,
                  correctAnswers: material.correctAnswers || [],
                  memoryPegCount: material.memoryPegCount || 0,
                  memoryPegLabels: material.memoryPegLabels || [],
                  numberTrackingCount: material.numberTrackingCount || 0,
                  numberTrackingDurationSec: material.numberTrackingDurationSec || DEFAULT_NUMBER_TRACKING_DURATION_SEC,
                  numberTrackingLineXPercent: material.numberTrackingLineXPercent ?? 50,
                  numberTrackingItems: Array.isArray(material.numberTrackingItems) ? material.numberTrackingItems : [],
                  eyeExerciseMode: String(material.eyeExerciseMode || '').trim(),
                  eyeExerciseDurationSec: Number(material.eyeExerciseDurationSec) || 0,
                  metronomeBpm: Number(material.metronomeBpm) || 0,
                  extractedQuestions: Array.isArray(material.extractedQuestions) ? material.extractedQuestions : [],
                  wordAlternativeExercises: Array.isArray(material.wordAlternativeExercises) ? material.wordAlternativeExercises : [],
                  balloonReadingItems: Array.isArray(material.balloonReadingItems) ? material.balloonReadingItems : [],
                  balloonReadingDurationSec: Number(material.balloonReadingDurationSec) || 180,
                  media: material.media || null,
                }))
                .filter(hasVisualMaterialSurface)
              : [],
          } : undefined,
        };
      })
      .filter((segment) => {
        if (!segment.audioUrl) return false;
        if (segment.kind === 'visual') return Boolean(segment.visualCheckpoint?.visualMaterials.length);
        if (segment.kind === 'quiz') return Boolean(segment.quiz && segment.quiz.question && segment.quiz.options.length === 4);
        return true;
      });
  }, [lesson.metadata?.lessonSegments]);

  const isSegmentedLesson = lessonSegments.length > 0;
  const currentSegment = isSegmentedLesson ? lessonSegments[Math.min(activeSegmentIndex, lessonSegments.length - 1)] : null;
  const isCurrentNumberTrackingVisualSegment = isNumberTrackingVisualSegment(currentSegment);

  const segmentOffsets = useMemo(() => {
    const offsets: number[] = [];
    let total = 0;
    for (const segment of lessonSegments) {
      offsets.push(total);
      total += getTimelineSegmentDuration(segment);
    }
    return offsets;
  }, [lessonSegments]);

  const totalSegmentDuration = useMemo(
    () => lessonSegments.reduce((sum, segment) => sum + getTimelineSegmentDuration(segment), 0),
    [lessonSegments]
  );
  const displayedLessonDurationMs = isSegmentedLesson
    ? Math.round(totalSegmentDuration * 1000)
    : lesson.audioDurationMs;

  const persistPlaybackSnapshot = (snapshotCurrentTime = currentTime, snapshotSegmentTime?: number) => {
    const audio = audioRef.current;
    const segmentDuration = getTimelineSegmentDuration(currentSegment);
    const localSegmentTime = Number.isFinite(snapshotSegmentTime)
      ? Math.max(0, Number(snapshotSegmentTime))
      : Math.max(0, Number(audio?.currentTime) || 0);
    const safeSegmentTime = segmentDuration > 0 ? Math.min(localSegmentTime, segmentDuration) : localSegmentTime;
    const nextSnapshot: LessonPlayerSnapshot = {
      activeSegmentIndex: Math.max(0, activeSegmentIndex),
      segmentTime: safeSegmentTime,
      currentTime: Math.max(0, Number(snapshotCurrentTime) || 0),
      rate,
      updatedAt: Date.now(),
    };
    latestSnapshotRef.current = nextSnapshot;
    writeLessonPlayerSnapshot(lesson.id, nextSnapshot);
  };

  const openMetronomeTab = (targetBpm?: number) => {
    const audio = audioRef.current;
    const segmentDuration = getTimelineSegmentDuration(currentSegment);
    const localTime = Math.max(0, Number(audio?.currentTime) || 0);
    const globalTime = isSegmentedLesson
      ? (segmentOffsets[activeSegmentIndex] || 0) + Math.min(localTime, segmentDuration)
      : localTime;
    persistPlaybackSnapshot(globalTime, localTime);
    pauseMainAudioByUser();
    if (targetBpm) configureSpeedReadingMetronome(targetBpm);
    navigate('?tab=metronome', { replace: false });
  };

  const lessonVisualMaterials = useMemo<LessonVisualMaterial[]>(() => {
    const materials = Array.isArray(lesson.metadata?.visualMaterials) ? lesson.metadata.visualMaterials : [];
    return [...materials]
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((material) => ({
        id: material.id,
        title: String(material.title || '').trim(),
        pageLabel: String(material.pageLabel || '').trim(),
        note: String(material.note || '').trim(),
        visualCategory: material.visualCategory || (material.isQuestionVisual ? 'question' : 'reading'),
        measurementTextRole: String(material.measurementTextRole || '').trim(),
        isQuestionVisual: getLessonVisualCategory(material) === 'question',
        trackTime: getLessonVisualCategory(material) === 'reading' ? material.trackTime !== false : false,
        questionCount: material.questionCount || 0,
        optionCount: material.optionCount || 0,
        correctAnswers: material.correctAnswers || [],
        memoryPegCount: material.memoryPegCount || 0,
        memoryPegLabels: material.memoryPegLabels || [],
        numberTrackingCount: material.numberTrackingCount || 0,
        numberTrackingDurationSec: material.numberTrackingDurationSec || DEFAULT_NUMBER_TRACKING_DURATION_SEC,
        numberTrackingLineXPercent: material.numberTrackingLineXPercent ?? 50,
        numberTrackingItems: Array.isArray(material.numberTrackingItems) ? material.numberTrackingItems : [],
        eyeExerciseMode: String(material.eyeExerciseMode || '').trim(),
        eyeExerciseDurationSec: Number(material.eyeExerciseDurationSec) || 0,
        metronomeBpm: Number(material.metronomeBpm) || 0,
        extractedQuestions: Array.isArray(material.extractedQuestions) ? material.extractedQuestions : [],
        wordAlternativeExercises: Array.isArray(material.wordAlternativeExercises) ? material.wordAlternativeExercises : [],
        balloonReadingItems: Array.isArray(material.balloonReadingItems) ? material.balloonReadingItems : [],
        balloonReadingDurationSec: Number(material.balloonReadingDurationSec) || 180,
        media: material.media || null,
      }))
      .filter(hasVisualMaterialSurface);
  }, [lesson.metadata?.visualMaterials]);

  const interactionQuizzes = useMemo<LessonInteractionQuiz[]>(() => {
    if (isSegmentedLesson) return [];
    const rawQuizzes = Array.isArray(lesson.metadata?.interactionQuizzes) ? lesson.metadata?.interactionQuizzes : [];
    return rawQuizzes
      .map((item, index) => {
        const ratioTime = lesson.audioDurationMs && item.ratio ? Math.round((lesson.audioDurationMs / 1000) * Number(item.ratio)) : 0;
        const timeSec = Math.max(8, Number(item.timeSec) || ratioTime || 0);
        return {
          id: String(item.id || `quiz_${index + 1}`),
          timeSec,
          sectionTitle: String(item.sectionTitle || '').trim(),
          question: String(item.question || '').trim(),
          options: Array.isArray(item.options) ? item.options.map((option) => String(option || '').trim()) : [],
          correctOptionIndex: Number(item.correctOptionIndex),
          wrongFeedback: String(item.wrongFeedback || '').trim(),
        };
      })
      .filter((item) => item.question.length >= 12 && item.options.length === 4 && item.timeSec > 0)
      .sort((a, b) => a.timeSec - b.timeSec);
  }, [isSegmentedLesson, lesson.audioDurationMs, lesson.metadata?.interactionQuizzes]);

  const clearPlaybackWatchdog = () => {
    if (playbackWatchdogRef.current !== null) {
      window.clearTimeout(playbackWatchdogRef.current);
      playbackWatchdogRef.current = null;
    }
  };

  const pauseTrackedMainAudio = (exceptAudio?: HTMLAudioElement | null) => {
    mainAudioElementsRef.current.forEach((audio) => {
      if (exceptAudio && audio === exceptAudio) return;
      try {
        audio.pause();
      } catch (_) {
        /* noop */
      }
    });
  };

  const resetMainAudioElement = async (resumeTime: number) => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    await new Promise((resolve) => window.setTimeout(resolve, 80));
    audio.src = audioUrl;
    audio.preload = 'auto';
    audio.load();
    await new Promise<void>((resolve) => {
      const done = () => {
        audio.removeEventListener('loadedmetadata', done);
        audio.removeEventListener('canplay', done);
        resolve();
      };
      audio.addEventListener('loadedmetadata', done, { once: true });
      audio.addEventListener('canplay', done, { once: true });
      window.setTimeout(done, 1500);
    });
    if (Number.isFinite(resumeTime) && resumeTime > 0) {
      try { audio.currentTime = resumeTime; } catch (_) { /* noop */ }
    }
  };

  const schedulePlaybackWatchdog = (startedAt: number) => {
    clearPlaybackWatchdog();
    playbackWatchdogRef.current = window.setTimeout(() => {
      const audio = audioRef.current;
      if (!audio || audio.paused || audio.ended) return;
      const advanced = audio.currentTime > startedAt + 0.12;
      if (advanced) return;

      if (mainAudioRepairingRef.current) {
        setIsPlaying(false);
        setMainAudioError('Ses başlatılamadı. Oynat düğmesine tekrar bas.');
        audio.pause();
        return;
      }

      mainAudioRepairingRef.current = true;
      const resumeTime = Number.isFinite(audio.currentTime) ? audio.currentTime : startedAt;
      resetMainAudioElement(resumeTime)
        .then(() => {
          if (userPausedRef.current) return undefined;
          return playMainAudio();
        })
        .catch((err) => {
          console.warn('Audio yeniden başlatılamadı:', err);
          setIsPlaying(false);
          setMainAudioError('Ses başlatılamadı. Oynat düğmesine tekrar bas.');
        })
        .finally(() => {
          window.setTimeout(() => {
            mainAudioRepairingRef.current = false;
          }, 600);
        });
    }, 1300);
  };

  const playMainAudio = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    userPausedRef.current = false;
    mainAudioElementsRef.current.add(audio);
    pauseTrackedMainAudio(audio);
    setMainAudioError(null);
    audio.muted = false;
    audio.volume = 1;
    audio.playbackRate = rate;
    if (audio.readyState < 2) audio.load();
    const startedAt = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
    await audio.play();
    schedulePlaybackWatchdog(startedAt);
  };

  const pauseMainAudioByUser = () => {
    const audio = audioRef.current;
    userPausedRef.current = true;
    autoPlayAfterSegmentChangeRef.current = false;
    mainAudioRepairingRef.current = false;
    clearPlaybackWatchdog();
    if (audio) mainAudioElementsRef.current.add(audio);
    pauseTrackedMainAudio();
    setIsPlaying(false);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    mainAudioElementsRef.current.add(audio);

    const onPlay = () => {
      setIsPlaying(true);
    };
    const onPause = () => {
      clearPlaybackWatchdog();
      setIsPlaying(false);
    };
    const onTime = () => {
      clearPlaybackWatchdog();
      setMainAudioError(null);
      mainAudioRepairingRef.current = false;
      let nextCurrentTime = audio.currentTime;
      if (isSegmentedLesson) {
        const segmentDuration = getTimelineSegmentDuration(currentSegment);
        nextCurrentTime = (segmentOffsets[activeSegmentIndex] || 0) + Math.min(audio.currentTime, segmentDuration);
        setCurrentTime(nextCurrentTime);
      } else {
        setCurrentTime(nextCurrentTime);
      }
      persistPlaybackSnapshot(nextCurrentTime, audio.currentTime);
    };
    const onMeta = () => {
      if (pendingSegmentSeekRef.current !== null) {
        audio.currentTime = pendingSegmentSeekRef.current;
        pendingSegmentSeekRef.current = null;
      }
      if (isSegmentedLesson) {
        setDuration(totalSegmentDuration || 0);
        if (autoPlayAfterSegmentChangeRef.current) {
          autoPlayAfterSegmentChangeRef.current = false;
          if (isCurrentNumberTrackingVisualSegment) return;
          if (userPausedRef.current) return;
          playMainAudio().catch(() => undefined);
        }
      } else {
        setDuration(audio.duration || 0);
      }
    };
    const onEnd = () => {
      clearPlaybackWatchdog();
      setIsPlaying(false);
      if (isSegmentedLesson && currentSegment) {
        setCurrentTime((segmentOffsets[activeSegmentIndex] || 0) + getTimelineSegmentDuration(currentSegment));
        if (currentSegment.visualCheckpoint) {
          if (isCurrentNumberTrackingVisualSegment) return;
          const firstVisualMaterial = currentSegment.visualCheckpoint.visualMaterials[0] || null;
          setPausedForQuestion(false);
          setSelectedOptionIndex(null);
          setInteractionResult(null);
          setInteractionError(null);
          setActiveInteraction(null);
          if (activeVisualCheckpoint?.id !== currentSegment.visualCheckpoint.id) {
            setActiveVisualCheckpoint(currentSegment.visualCheckpoint);
            setVisualPageIndex(0);
            setMaxVisualPageSeen(0);
            setVisualZoom(1);
            setVisualQuestionIndex(0);
            setVisualAnswers({});
            setVisualMemoryPegAnswers({});
            setVisualWordAlternativeAnswers({});
            setVisualWordAlternativeSelections({});
            setVisualPageTimes({});
            visualPageTimesRef.current = {};
          }
          startVisualTimer(firstVisualMaterial);
          resetNumberTrackingExercise(null, false);
          return;
        }
        if (currentSegment.quiz) {
          setPausedForQuestion(false);
          setSelectedOptionIndex(null);
          setInteractionResult(null);
          setInteractionError(null);
          setActiveVisualCheckpoint(null);
          setActiveInteraction(currentSegment.quiz);
          return;
        }
        const nextIndex = activeSegmentIndex + 1;
        if (nextIndex < lessonSegments.length) {
          autoPlayAfterSegmentChangeRef.current = true;
          setActiveSegmentIndex(nextIndex);
        }
      }
    };
    const onWaiting = () => {
      if (!audio.paused) schedulePlaybackWatchdog(audio.currentTime || 0);
    };
    const onError = () => {
      clearPlaybackWatchdog();
      setIsPlaying(false);
      setMainAudioError('Ses yüklenemedi. Oynat düğmesine tekrar bas.');
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('stalled', onWaiting);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('stalled', onWaiting);
      audio.removeEventListener('error', onError);
      if (audioRef.current !== audio) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        mainAudioElementsRef.current.delete(audio);
      }
    };
  }, [activeSegmentIndex, activeVisualCheckpoint, currentSegment, isCurrentNumberTrackingVisualSegment, isSegmentedLesson, lessonSegments.length, segmentOffsets, totalSegmentDuration]);

  useEffect(() => {
    if (isSegmentedLesson) {
      setDuration(totalSegmentDuration || 0);
      setCurrentTime(segmentOffsets[activeSegmentIndex] || 0);
    }
  }, [activeSegmentIndex, isSegmentedLesson, segmentOffsets, totalSegmentDuration]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
    const snapshot = latestSnapshotRef.current;
    if (snapshot) {
      latestSnapshotRef.current = { ...snapshot, rate, updatedAt: Date.now() };
      writeLessonPlayerSnapshot(lesson.id, latestSnapshotRef.current);
    }
  }, [rate]);

  useEffect(() => {
    if (restoredSnapshotRef.current) return;
    const snapshot = initialSnapshotRef.current;
    if (!snapshot) {
      restoredSnapshotRef.current = true;
      return;
    }
    if (isSegmentedLesson && !lessonSegments.length) return;

    const safeSegmentIndex = isSegmentedLesson
      ? Math.max(0, Math.min(lessonSegments.length - 1, snapshot.activeSegmentIndex))
      : 0;
    pendingSegmentSeekRef.current = snapshot.segmentTime;
    if (safeSegmentIndex !== activeSegmentIndex) {
      setActiveSegmentIndex(safeSegmentIndex);
    } else if (audioRef.current) {
      try { audioRef.current.currentTime = snapshot.segmentTime; } catch (_) { /* noop */ }
    }
    setCurrentTime(isSegmentedLesson
      ? (segmentOffsets[safeSegmentIndex] || 0) + snapshot.segmentTime
      : snapshot.currentTime
    );
    restoredSnapshotRef.current = true;
  }, [activeSegmentIndex, isSegmentedLesson, lessonSegments.length, segmentOffsets]);

  useEffect(() => {
    if (!interactionQuizzes.length || showQuestion || activeInteraction || activeVisualCheckpoint) return;
    const nextQuiz = interactionQuizzes.find((item) => currentTime >= item.timeSec && !seenInteractionsRef.current.has(item.id));
    if (!nextQuiz) return;

    seenInteractionsRef.current.add(nextQuiz.id);
    setActiveInteraction(nextQuiz);
    setSelectedOptionIndex(null);
    setInteractionResult(null);
    setInteractionError(null);
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      setPausedForQuestion(true);
      audio.pause();
    }
  }, [activeInteraction, activeVisualCheckpoint, currentTime, interactionQuizzes, showQuestion]);

  useEffect(() => () => {
    writeLessonPlayerSnapshot(lesson.id, latestSnapshotRef.current);
    clearPlaybackWatchdog();
    pauseTrackedMainAudio();
    if (feedbackAudioRef.current) {
      feedbackAudioRef.current.pause();
      feedbackAudioRef.current.removeAttribute('src');
      feedbackAudioRef.current.load();
    }
    if (feedbackObjectUrlRef.current) {
      URL.revokeObjectURL(feedbackObjectUrlRef.current);
      feedbackObjectUrlRef.current = null;
    }
    if (numberTrackingTransitionTimeoutRef.current !== null) {
      window.clearTimeout(numberTrackingTransitionTimeoutRef.current);
      numberTrackingTransitionTimeoutRef.current = null;
    }
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isCurrentNumberTrackingVisualSegment && activeVisualCheckpoint && !numberTrackingFinished) {
      pauseMainAudioByUser();
      return;
    }
    if (audio.paused) {
      try {
        await playMainAudio();
      } catch (err) {
        console.warn('Audio play hata:', err);
      }
    } else {
      pauseMainAudioByUser();
    }
  };

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const value = Number(event.target.value);
    if (isSegmentedLesson) {
      const segmentIndex = lessonSegments.findIndex((segment, index) => {
        const start = segmentOffsets[index] || 0;
        const segmentDuration = getTimelineSegmentDuration(segment);
        const end = start + segmentDuration;
        return segmentDuration <= 0 ? value === start : value >= start && value <= end;
      });
      const safeIndex = segmentIndex >= 0 ? segmentIndex : Math.max(0, lessonSegments.length - 1);
      const localTime = Math.max(0, value - (segmentOffsets[safeIndex] || 0));
      setActiveInteraction(null);
      setActiveVisualCheckpoint(null);
      setSelectedOptionIndex(null);
      setInteractionResult(null);
      setInteractionError(null);
      setVisualPageIndex(0);
      setMaxVisualPageSeen(0);
      setVisualQuestionIndex(0);
      setVisualAnswers({});
      setVisualMemoryPegAnswers({});
      setVisualWordAlternativeAnswers({});
      setVisualWordAlternativeSelections({});
      setVisualPageTimes({});
      setNumberTrackingPhase(1);
      setNumberTrackingTimeLeft(DEFAULT_NUMBER_TRACKING_DURATION_SEC);
      setNumberTrackingRunning(false);
      setNumberTrackingTransition(false);
      setNumberTrackingFinished(false);
      setNumberTrackingTargets({ 1: 1, 2: 1 });
      setNumberTrackingSelections({ 1: [], 2: [] });
      numberTrackingPhaseEndHandledRef.current = false;
      openedVisualCheckpointSegmentRef.current = null;
      if (numberTrackingTransitionTimeoutRef.current !== null) {
        window.clearTimeout(numberTrackingTransitionTimeoutRef.current);
        numberTrackingTransitionTimeoutRef.current = null;
      }
      visualPageTimesRef.current = {};
      visualPageStartedAtRef.current = null;
      if (safeIndex !== activeSegmentIndex) {
        pendingSegmentSeekRef.current = localTime;
        setActiveSegmentIndex(safeIndex);
      } else {
        audio.currentTime = localTime;
      }
      setCurrentTime(value);
      persistPlaybackSnapshot(value, localTime);
      return;
    }
    audio.currentTime = value;
    setCurrentTime(value);
    persistPlaybackSnapshot(value, value);
  };

  const skip = (delta: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isSegmentedLesson) {
      const nextTime = Math.max(0, Math.min(duration, currentTime + delta));
      const syntheticEvent = {
        target: { value: String(nextTime) },
      } as React.ChangeEvent<HTMLInputElement>;
      handleSeek(syntheticEvent);
      return;
    }
    audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + delta));
  };

  const openQuestion = () => {
    const wasInteractionPause = Boolean(activeInteraction);
    setActiveInteraction(null);
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      setPausedForQuestion(true);
      audio.pause();
    } else {
      setPausedForQuestion((prev) => prev || wasInteractionPause);
    }
    setShowQuestion(true);
  };

  const resumeAfterQuestion = () => {
    if (pausedForQuestion && audioRef.current) {
      playMainAudio().catch(() => undefined);
    }
    setPausedForQuestion(false);
  };

  const continueAfterInteraction = () => {
    if (feedbackAudioRef.current) {
      feedbackAudioRef.current.pause();
      feedbackAudioRef.current.currentTime = 0;
      feedbackAudioRef.current.removeAttribute('src');
      feedbackAudioRef.current.load();
    }
    const completedSegmentIndex = activeInteraction?.segmentIndex;
    setActiveInteraction(null);
    setSelectedOptionIndex(null);
    setInteractionResult(null);
    setInteractionError(null);
    setFeedbackAudioSrc(null);
    setFeedbackAudioError(null);
    setIsFeedbackPlaying(false);
    if (feedbackObjectUrlRef.current) {
      URL.revokeObjectURL(feedbackObjectUrlRef.current);
      feedbackObjectUrlRef.current = null;
    }
    if (isSegmentedLesson && completedSegmentIndex !== undefined) {
      const nextIndex = completedSegmentIndex + 1;
      if (nextIndex < lessonSegments.length) {
        userPausedRef.current = false;
        autoPlayAfterSegmentChangeRef.current = true;
        setActiveSegmentIndex(nextIndex);
      }
      setPausedForQuestion(false);
      return;
    }
    resumeAfterQuestion();
  };

  const getVisualMaterialKey = (material: LessonVisualMaterial, index: number) => (
    material.id || `${material.pageLabel}_${index}`
  );

  const shouldTrackVisualTime = (material: LessonVisualMaterial | null) => (
    Boolean(material && getLessonVisualCategory(material) === 'reading' && material.trackTime !== false)
  );

  const getMemoryPegKey = (material: LessonVisualMaterial, label: string) => (
    `${material.id || material.pageLabel}_peg_${label}`
  );

  const startVisualTimer = (material: LessonVisualMaterial | null) => {
    visualPageStartedAtRef.current = shouldTrackVisualTime(material) ? Date.now() : null;
  };

  const recordCurrentVisualPageTime = () => {
    if (!activeVisualMaterial || visualPageStartedAtRef.current === null) return visualPageTimesRef.current;
    if (!shouldTrackVisualTime(activeVisualMaterial)) return visualPageTimesRef.current;
    const elapsedSeconds = Math.max(0, Math.round((Date.now() - visualPageStartedAtRef.current) / 1000));
    const key = getVisualMaterialKey(activeVisualMaterial, visualPageIndex);
    const nextTimes = {
      ...visualPageTimesRef.current,
      [key]: (visualPageTimesRef.current[key] || 0) + elapsedSeconds,
    };
    visualPageTimesRef.current = nextTimes;
    setVisualPageTimes(nextTimes);
    visualPageStartedAtRef.current = null;
    return nextTimes;
  };

  const advanceAfterVisualCheckpoint = () => {
    if (!activeVisualCheckpoint) return;
    const completedSegmentIndex = activeVisualCheckpoint.segmentIndex;
    setActiveVisualCheckpoint(null);
    setVisualPageIndex(0);
    setMaxVisualPageSeen(0);
    setVisualZoom(1);
    setVisualQuestionIndex(0);
    setVisualAnswers({});
    setVisualMemoryPegAnswers({});
    setVisualWordAlternativeAnswers({});
    setVisualWordAlternativeSelections({});
    setVisualPageTimes({});
    resetNumberTrackingExercise(null, false);
    openedVisualCheckpointSegmentRef.current = null;
    visualPageTimesRef.current = {};
    visualPageStartedAtRef.current = null;
    if (isSegmentedLesson) {
      const nextIndex = completedSegmentIndex + 1;
      if (nextIndex < lessonSegments.length) {
        userPausedRef.current = false;
        autoPlayAfterSegmentChangeRef.current = true;
        setActiveSegmentIndex(nextIndex);
      }
    }
  };

  const continueAfterVisualCheckpoint = () => {
    if (!activeVisualCheckpoint) return;
    const pageTimesSnapshot = recordCurrentVisualPageTime();
    const numberTrackingSummary = isActiveNumberTracking && activeVisualMaterial
      ? ([numberTrackingPhase] as const).map((phase) => ({
        materialTitle: activeVisualMaterial.title,
        materialPageLabel: activeVisualMaterial.pageLabel,
        phase,
        foundCount: numberTrackingSelections[phase]?.length || 0,
        totalCount: activeNumberTrackingTotalCount,
        seconds: getNumberTrackingDuration(activeVisualMaterial),
      }))
      : [];
    if (activeVisualQuestions.length || activeMemoryPegEntries.length || activeWordAlternativeEntries.length || numberTrackingSummary.length) {
      setVisualSummary({
        title: activeVisualCheckpoint.title || 'Görsel çalışma sonucu',
        pageTimes: activeVisualMaterials
          .map((material, index) => ({
            title: material.title,
            pageLabel: material.pageLabel,
            seconds: pageTimesSnapshot[getVisualMaterialKey(material, index)] || 0,
          }))
          .filter((item, index) => shouldTrackVisualTime(activeVisualMaterials[index])),
        questions: activeVisualQuestions.map((question) => ({
          ...question,
          selectedOptionIndex: visualAnswers[question.key] ?? null,
        })),
        memoryPegs: activeMemoryPegEntries.map((entry) => ({
          ...entry,
          value: visualMemoryPegAnswers[entry.key]?.trim() || '',
        })),
        wordAlternatives: activeWordAlternativeEntries.map((entry) => ({
          materialTitle: entry.materialTitle,
          materialPageLabel: entry.materialPageLabel,
          label: entry.exercise.label,
          answers: Array.from({ length: getWordAlternativeBlankCount(entry.exercise) }).map((_, blankIndex) => (
            getWordAlternativeOptionWord(entry.exercise, visualWordAlternativeSelections[entry.key]?.[blankIndex])
          )),
        })),
        numberTracking: numberTrackingSummary,
      });
      return;
    }
    advanceAfterVisualCheckpoint();
  };

  const showVisualPage = (nextIndex: number) => {
    if (!activeVisualCheckpoint) return;
    recordCurrentVisualPageTime();
    const pageCount = activeVisualCheckpoint.visualMaterials.length;
    const safeIndex = Math.max(0, Math.min(pageCount - 1, nextIndex));
    setVisualPageIndex(safeIndex);
    setMaxVisualPageSeen((prev) => Math.max(prev, safeIndex));
    const nextMaterial = activeVisualCheckpoint.visualMaterials[safeIndex] || null;
    startVisualTimer(nextMaterial);
    if (getLessonVisualCategory(nextMaterial) === 'number_tracking') {
      resetNumberTrackingExercise(nextMaterial, true, getNumberTrackingCheckpointRound(activeVisualCheckpoint));
    } else {
      resetNumberTrackingExercise(null, false);
    }
  };

  const changeVisualZoom = (delta: number) => {
    setVisualZoom((prev) => Math.max(0.8, Math.min(2.4, Number((prev + delta).toFixed(2)))));
  };

  const selectVisualAnswer = (questionKey: string, optionIndex: number) => {
    setVisualAnswers((prev) => ({ ...prev, [questionKey]: optionIndex }));
  };

  const updateMemoryPegAnswer = (key: string, value: string) => {
    setVisualMemoryPegAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const updateWordAlternativeAnswer = (key: string, value: string) => {
    setVisualWordAlternativeAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const getWordAlternativeExerciseKey = (material: LessonVisualMaterial, exercise: WordAlternativeExercise) => (
    `${material.id || material.pageLabel}_word_alternatives_${exercise.index}_${exercise.label}`
  );

  const getWordAlternativeBlankCount = (exercise: WordAlternativeExercise) => (
    Math.max(0, exercise.sentenceParts.length - 1)
  );

  const getWordAlternativeOptionWord = (exercise: WordAlternativeExercise, optionNumber: number | null | undefined) => (
    exercise.options.find((option) => option.number === optionNumber)?.word || ''
  );

  const assignWordAlternativeOption = (exerciseKey: string, exercise: WordAlternativeExercise, blankIndex: number, optionNumber: number) => {
    setVisualWordAlternativeSelections((prev) => {
      const blankCount = getWordAlternativeBlankCount(exercise);
      const current = Array.from({ length: blankCount }).map((_, index) => prev[exerciseKey]?.[index] ?? null);
      const next = current.map((value, index) => (value === optionNumber && index !== blankIndex ? null : value));
      next[blankIndex] = optionNumber;
      return { ...prev, [exerciseKey]: next };
    });
  };

  const clearWordAlternativeBlank = (exerciseKey: string, blankIndex: number) => {
    setVisualWordAlternativeSelections((prev) => {
      const current = [...(prev[exerciseKey] || [])];
      current[blankIndex] = null;
      return { ...prev, [exerciseKey]: current };
    });
  };

  const fillNextWordAlternativeBlank = (exerciseKey: string, exercise: WordAlternativeExercise, optionNumber: number) => {
    const blankCount = getWordAlternativeBlankCount(exercise);
    const current = Array.from({ length: blankCount }).map((_, index) => (
      visualWordAlternativeSelections[exerciseKey]?.[index] ?? null
    ));
    if (current.includes(optionNumber)) return;
    const nextBlankIndex = current.findIndex((value) => value === null);
    if (nextBlankIndex < 0) return;
    assignWordAlternativeOption(exerciseKey, exercise, nextBlankIndex, optionNumber);
  };

  const handleWordAlternativeDrop = (
    event: React.DragEvent<HTMLButtonElement>,
    exerciseKey: string,
    exercise: WordAlternativeExercise,
    blankIndex: number
  ) => {
    event.preventDefault();
    const optionNumber = Number(event.dataTransfer.getData('text/plain'));
    if (!Number.isInteger(optionNumber)) return;
    assignWordAlternativeOption(exerciseKey, exercise, blankIndex, optionNumber);
  };

  const getNumberTrackingDuration = (material: LessonVisualMaterial | null) => (
    Math.max(10, Math.min(300, Number(material?.numberTrackingDurationSec) || DEFAULT_NUMBER_TRACKING_DURATION_SEC))
  );

  const resetNumberTrackingExercise = (material: LessonVisualMaterial | null, shouldRun = true, phase: 1 | 2 = 1) => {
    if (numberTrackingTransitionTimeoutRef.current !== null) {
      window.clearTimeout(numberTrackingTransitionTimeoutRef.current);
      numberTrackingTransitionTimeoutRef.current = null;
    }
    const durationSec = getNumberTrackingDuration(material);
    setNumberTrackingPhase(phase);
    setNumberTrackingTimeLeft(durationSec);
    setNumberTrackingRunning(Boolean(shouldRun && material && getLessonVisualCategory(material) === 'number_tracking'));
    setNumberTrackingTransition(false);
    setNumberTrackingFinished(false);
    setNumberTrackingTargets({ 1: 1, 2: 1 });
    setNumberTrackingSelections({ 1: [], 2: [] });
    numberTrackingPhaseEndHandledRef.current = false;
  };

  useEffect(() => {
    if (!isSegmentedLesson || !currentSegment?.visualCheckpoint) return;
    if (openedVisualCheckpointSegmentRef.current === activeSegmentIndex) return;

    const checkpoint = currentSegment.visualCheckpoint;
    const firstVisualMaterial = checkpoint.visualMaterials[0] || null;

    openedVisualCheckpointSegmentRef.current = activeSegmentIndex;

    setPausedForQuestion(false);
    setSelectedOptionIndex(null);
    setInteractionResult(null);
    setInteractionError(null);
    setActiveInteraction(null);
    setActiveVisualCheckpoint(checkpoint);
    setVisualPageIndex(0);
    setMaxVisualPageSeen(0);
    setVisualZoom(1);
    setVisualQuestionIndex(0);
    setVisualAnswers({});
    setVisualMemoryPegAnswers({});
    setVisualWordAlternativeAnswers({});
    setVisualWordAlternativeSelections({});
    setVisualPageTimes({});
    visualPageTimesRef.current = {};
    visualPageStartedAtRef.current = null;
    if (getLessonVisualCategory(firstVisualMaterial) === 'number_tracking') {
      resetNumberTrackingExercise(
        firstVisualMaterial,
        true,
        getNumberTrackingCheckpointRound(checkpoint)
      );
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        try { audio.currentTime = 0; } catch (_) { /* noop */ }
      }
      setCurrentTime(segmentOffsets[activeSegmentIndex] || 0);
    } else {
      resetNumberTrackingExercise(null, false);
    }
  }, [activeSegmentIndex, currentSegment, isSegmentedLesson, segmentOffsets]);

  const finishNumberTrackingPhase = () => {
    if (numberTrackingPhaseEndHandledRef.current) return;
    numberTrackingPhaseEndHandledRef.current = true;
    setNumberTrackingRunning(false);
    setNumberTrackingTransition(false);
    setNumberTrackingFinished(true);
  };

  const selectNumberTrackingTarget = (value: number) => {
    if (!numberTrackingRunning || numberTrackingTransition || numberTrackingFinished) return;
    const expectedValue = numberTrackingTargets[numberTrackingPhase] || 1;
    if (value !== expectedValue) return;

    const totalCount = activeNumberTrackingTotalCount;
    setNumberTrackingSelections((prev) => ({
      ...prev,
      [numberTrackingPhase]: [...(prev[numberTrackingPhase] || []), value],
    }));
    setNumberTrackingTargets((prev) => ({
      ...prev,
      [numberTrackingPhase]: expectedValue + 1,
    }));

    if (totalCount && expectedValue >= totalCount) {
      finishNumberTrackingPhase();
    }
  };

  const base64ToBlob = (base64: string, mimeType: string): Blob => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mimeType });
  };

  const startFeedbackAudioSource = (url: string) => {
    const audio = feedbackAudioRef.current;
    setFeedbackAudioError(null);
    setIsFeedbackPlaying(true);
    setFeedbackAudioSrc(url);
    if (!audio) {
      setIsFeedbackPlaying(false);
      setFeedbackAudioError('Açıklama sesi başlatılamadı.');
      return;
    }

    audio.pause();
    if (audio.src !== url) audio.src = url;
    audio.currentTime = 0;
    audio.load();
    audio.play().catch((err) => {
      console.warn('Geri bildirim sesi otomatik başlatılamadı:', err);
      setIsFeedbackPlaying(false);
      setFeedbackAudioError('Açıklama sesi başlatılamadı. Tekrar oynat düğmesine bas.');
    });
  };

  const playFeedbackAudio = (base64: string, mimeType: string) => {
    if (feedbackObjectUrlRef.current) {
      URL.revokeObjectURL(feedbackObjectUrlRef.current);
      feedbackObjectUrlRef.current = null;
    }
    const blob = base64ToBlob(base64, mimeType);
    const url = URL.createObjectURL(blob);
    feedbackObjectUrlRef.current = url;
    startFeedbackAudioSource(url);
  };

  const playFeedbackAudioUrl = (url: string) => {
    if (feedbackObjectUrlRef.current) {
      URL.revokeObjectURL(feedbackObjectUrlRef.current);
      feedbackObjectUrlRef.current = null;
    }
    startFeedbackAudioSource(url);
  };

  const replayFeedbackAudio = () => {
    const audio = feedbackAudioRef.current;
    if (!audio || !feedbackAudioSrc) return;
    setFeedbackAudioError(null);
    audio.currentTime = 0;
    audio.play().catch((err) => {
      console.warn('Geri bildirim sesi oynatılamadı:', err);
      setIsFeedbackPlaying(false);
      setFeedbackAudioError('Açıklama sesi oynatılamadı.');
    });
  };

  const handleInteractionAnswer = async (optionIndex: number) => {
    if (!activeInteraction || interactionLoading || interactionResult) return;
    const selectedWrongOption = optionIndex !== activeInteraction.correctOptionIndex;
    const startedLocalFeedbackAudio = Boolean(selectedWrongOption && activeInteraction.feedbackAudioUrl);
    setSelectedOptionIndex(optionIndex);
    setInteractionLoading(true);
    setInteractionError(null);
    if (startedLocalFeedbackAudio && activeInteraction.feedbackAudioUrl) {
      playFeedbackAudioUrl(activeInteraction.feedbackAudioUrl);
    }
    try {
      const result = await TrainingApi.answerInteraction(lesson.id, activeInteraction.id, optionIndex);
      setInteractionResult(result);
      if (!result.correct && result.feedbackAudioUrl && !startedLocalFeedbackAudio) {
        playFeedbackAudioUrl(result.feedbackAudioUrl);
      } else if (!result.correct && result.audioBase64 && result.mimeType && !startedLocalFeedbackAudio) {
        playFeedbackAudio(result.audioBase64, result.mimeType);
      }
    } catch (err: any) {
      setInteractionError(err?.message || 'Cevap kontrol edilemedi');
    } finally {
      setInteractionLoading(false);
    }
  };

  const formatSeconds = (value: number) => {
    if (!Number.isFinite(value)) return '0:00';
    const total = Math.max(0, Math.floor(value));
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const audioUrl = isSegmentedLesson ? (currentSegment?.audioUrl || '') : (lesson.audioMedia?.url || '');
  const selectedWrongByMetadata = Boolean(
    activeInteraction &&
    selectedOptionIndex !== null &&
    selectedOptionIndex !== activeInteraction.correctOptionIndex
  );
  const showWrongFeedbackAudio = Boolean(
    (interactionResult && !interactionResult.correct) ||
    (selectedWrongByMetadata && feedbackAudioSrc)
  );
  const activeVisualMaterials = activeVisualCheckpoint?.visualMaterials || [];
  const activeVisualMaterial = activeVisualMaterials[visualPageIndex] || null;
  const activeVisualPageCount = activeVisualMaterials.length;
  const activeVisualQuestions = activeVisualMaterials.flatMap((material) => (
    (getLessonVisualCategory(material) === 'question' || (getLessonVisualCategory(material) === 'measurement_text' && material.measurementTextRole === 'question')) && Array.isArray(material.extractedQuestions)
      ? material.extractedQuestions.map((question, index) => ({
        key: `${material.id || material.pageLabel}_${question.index || index + 1}`,
        materialTitle: material.title,
        materialPageLabel: material.pageLabel,
        questionIndex: question.index || index + 1,
        question: String(question.question || '').trim(),
        options: Array.isArray(question.options) ? question.options.map((option) => String(option || '').trim()).filter(Boolean) : [],
        correctOptionIndex: Number.isInteger(question.correctOptionIndex) ? Number(question.correctOptionIndex) : null,
      }))
      : []
  )).filter((question) => question.question && question.options.length >= 2);
  const activeVisualQuestion = activeVisualQuestions[Math.min(visualQuestionIndex, Math.max(0, activeVisualQuestions.length - 1))] || null;
  const answeredVisualQuestionCount = activeVisualQuestions.filter((question) => visualAnswers[question.key] !== undefined).length;
  const activeMeasurementTextMaterials = activeVisualMaterials.filter((material) => (
    getLessonVisualCategory(material) === 'measurement_text' && material.measurementTextRole !== 'question'
  ));
  const isActiveMeasurementText = Boolean(activeMeasurementTextMaterials.length);
  const activeMemoryPegEntries = activeVisualMaterials.flatMap((material) => {
    if (getLessonVisualCategory(material) !== 'memory_pegs') return [];
    return getMemoryPegLabels(material).map((label) => ({
      key: getMemoryPegKey(material, label),
      materialTitle: material.title,
      materialPageLabel: material.pageLabel,
      label,
    }));
  });
  const answeredMemoryPegCount = activeMemoryPegEntries.filter((entry) => visualMemoryPegAnswers[entry.key]?.trim()).length;
  const activeWordAlternativeEntries = activeVisualMaterials
    .filter((material) => getLessonVisualCategory(material) === 'word_alternatives')
    .flatMap((material) => (
      (Array.isArray(material.wordAlternativeExercises) ? material.wordAlternativeExercises : []).map((exercise) => ({
        key: getWordAlternativeExerciseKey(material, exercise),
        materialTitle: material.title,
        materialPageLabel: material.pageLabel,
        exercise,
      }))
    ));
  const answeredWordAlternativeCount = activeWordAlternativeEntries
    .filter((entry) => {
      const blankCount = getWordAlternativeBlankCount(entry.exercise);
      const selections = visualWordAlternativeSelections[entry.key] || [];
      return blankCount > 0 && Array.from({ length: blankCount }).every((_, index) => selections[index] !== null && selections[index] !== undefined);
    }).length;
  const activeMaterialWordAlternativeEntries = activeWordAlternativeEntries.filter((entry) => (
    activeVisualMaterial &&
    entry.materialPageLabel === activeVisualMaterial.pageLabel &&
    entry.materialTitle === activeVisualMaterial.title
  ));
  const activeNumberTrackingItems = getNumberTrackingItems(activeVisualMaterial);
  const activeNumberTrackingTotalCount = Math.max(
    Number(activeVisualMaterial?.numberTrackingCount) || 0,
    activeNumberTrackingItems.length ? Math.max(...activeNumberTrackingItems.map((item) => item.value)) : 0
  );
  const activeNumberTrackingSelections = numberTrackingSelections[numberTrackingPhase] || [];
  const activeNumberTrackingFoundCount = activeNumberTrackingSelections.length;
  const isActiveNumberTracking = Boolean(activeVisualMaterial && getLessonVisualCategory(activeVisualMaterial) === 'number_tracking');
  const shouldShowNumberTrackingGuideLines = Boolean(
    isActiveNumberTracking && getNumberTrackingCheckpointRound(activeVisualCheckpoint) === 2
  );
  const canContinueVisual = Boolean(
    activeVisualCheckpoint &&
    (!activeVisualCheckpoint.requireLastPage || maxVisualPageSeen >= activeVisualPageCount - 1) &&
    (!isActiveMeasurementText) &&
    (!activeVisualQuestions.length || answeredVisualQuestionCount === activeVisualQuestions.length) &&
    (!activeMemoryPegEntries.length || answeredMemoryPegCount === activeMemoryPegEntries.length) &&
    (!activeWordAlternativeEntries.length || answeredWordAlternativeCount === activeWordAlternativeEntries.length) &&
    (!isActiveNumberTracking || numberTrackingFinished)
  );
  const shouldShowVisualPanel = Boolean(activeVisualCheckpoint || lessonVisualMaterials.length);

  useEffect(() => {
    if (!isActiveNumberTracking || !numberTrackingRunning || numberTrackingTransition || numberTrackingFinished) return undefined;
    const interval = window.setInterval(() => {
      setNumberTrackingTimeLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(interval);
          window.setTimeout(() => finishNumberTrackingPhase(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [isActiveNumberTracking, numberTrackingFinished, numberTrackingPhase, numberTrackingRunning, numberTrackingTransition]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f2f6f8] sm:h-full">

      {/* ── Top nav bar ── */}
      <div className="hidden">
        <button
          type="button"
          onClick={onBack}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#eef6f8] text-[#174e67] transition hover:bg-[#dff0f3] hover:text-[#0ea5b7]"
          aria-label="Geri dön"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0ea5b7]">Hızlı Okuma · Eğitim</p>
          <h1 className="truncate text-[13px] font-extrabold leading-tight text-[#174e67]">{lesson.title}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden items-center gap-1.5 rounded-full bg-[#eef6f8] px-3 py-1.5 text-[11px] font-bold text-[#174e67] sm:inline-flex">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            {formatLessonDuration(displayedLessonDurationMs)}
          </span>
          <span className="hidden rounded-full bg-[#eef6f8] px-3 py-1.5 text-[11px] font-bold text-[#174e67] sm:inline-block">{lesson.voice}</span>
        </div>
      </div>

      {/* ── Main content row ── */}
      <div className="flex flex-1 flex-col overflow-auto sm:flex-row sm:overflow-hidden">

        {/* LEFT: Audio sidebar */}
        <div className="flex w-full shrink-0 flex-col border-b border-[#dbe7ec] bg-white sm:w-[220px] sm:overflow-y-auto sm:border-b-0 sm:border-r md:w-[260px] lg:w-[300px]">

          {/* Waveform — cover'ın yerinde */}
          <div className="relative h-24 shrink-0 overflow-hidden bg-[#0b1f29] sm:h-36">
            <button
              type="button"
              onClick={onBack}
              className="absolute left-3 top-3 z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/90 text-[#174e67] shadow-sm transition hover:bg-white hover:text-[#0ea5b7]"
              aria-label="Eğitim listesine dön"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
            </button>
            <VoiceWaveform isActive={isPlaying} barCount={36} />
          </div>

          {lesson.description ? (
            <div className="px-5 pt-4 pb-2">
              <p className="text-[13px] leading-5 text-[#5d7681]">{lesson.description}</p>
            </div>
          ) : null}

          <div className="flex-1" />

          {/* Audio controls section */}
          <div className="shrink-0 border-t border-[#ecf2f5] bg-[#f7fbfc] px-4 py-4">
            {mainAudioError ? (
              <div className="mt-3 rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm font-semibold text-[#a04c4c]">{mainAudioError}</div>
            ) : null}

            <audio key={audioUrl} ref={audioRef} src={audioUrl} preload="auto" crossOrigin="anonymous" />
            <audio
              ref={feedbackAudioRef}
              preload="auto"
              className="hidden"
              onPlay={() => {
                setFeedbackAudioError(null);
                setIsFeedbackPlaying(true);
              }}
              onPause={() => setIsFeedbackPlaying(false)}
              onEnded={() => setIsFeedbackPlaying(false)}
              onError={() => {
                setIsFeedbackPlaying(false);
                if (feedbackAudioSrc) setFeedbackAudioError('Açıklama sesi yüklenemedi veya oynatılamadı.');
              }}
            />

            <div className="mt-3 flex flex-col gap-2">
              <input
                type="range"
                min={0}
                max={duration || 0}
                step={0.5}
                value={Math.min(currentTime, duration || 0)}
                onChange={handleSeek}
                style={{ ['--progress' as string]: `${duration ? Math.min(100, (currentTime / duration) * 100) : 0}%` }}
                className="speed-reading-lesson-progress w-full"
                aria-label="Ders ilerlemesi"
              />
              <div className="flex items-center justify-between text-xs font-semibold text-[#67808c]">
                <span>{formatSeconds(currentTime)}</span>
                <span>{formatSeconds(duration)}</span>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => skip(-10)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#174e67] shadow-sm ring-1 ring-[#dbe7ec] transition hover:bg-[#eef6f8]"
                  aria-label="10 saniye geri"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 17 6 12l5-5" /><path d="M18 17 13 12l5-5" /></svg>
                </button>
                <button
                  type="button"
                  onClick={togglePlay}
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[#0ea5b7] to-[#174e67] text-white shadow-[0_10px_28px_rgba(14,165,183,0.4)] transition active:scale-95"
                  aria-label={isPlaying ? 'Durdur' : 'Oynat'}
                >
                  {isPlaying ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => skip(10)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#174e67] shadow-sm ring-1 ring-[#dbe7ec] transition hover:bg-[#eef6f8]"
                  aria-label="10 saniye ileri"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m13 17 5-5-5-5" /><path d="m6 17 5-5-5-5" /></svg>
                </button>
              </div>

              <div className="inline-flex items-center gap-0.5 rounded-full bg-white p-1 text-xs font-bold shadow-sm ring-1 ring-[#dbe7ec]">
                {PLAYBACK_RATES.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRate(value)}
                    className={`rounded-full px-2.5 py-1.5 transition ${rate === value ? 'bg-[#174e67] text-white' : 'text-[#67808c] hover:text-[#174e67]'
                      }`}
                  >
                    {value}x
                  </button>
                ))}
              </div>
            </div>
          </div>
          {/* ── END audio sidebar ── */}
        </div>

        {/* RIGHT: Visual panel — takes all remaining space */}
        {shouldShowVisualPanel ? (
          <aside className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#ecf2f5] bg-gradient-to-r from-[#f0f8fa] to-[#f7fbfc] px-4 py-2">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#0ea5b7]">Görsel çalışma</p>
                <h3 className="mt-0.5 text-sm font-black leading-tight text-[#174e67]">
                  {activeVisualCheckpoint?.title || 'Ders görselleri'}
                </h3>
              </div>
              {activeVisualCheckpoint ? (
                <span className="shrink-0 rounded-full bg-white px-3 py-1.5 text-[11px] font-black text-[#174e67] shadow-sm ring-1 ring-[#dbe7ec]">
                  {activeVisualPageCount ? `${visualPageIndex + 1} / ${activeVisualPageCount}` : '0 / 0'}
                </span>
              ) : null}
            </div>

            {activeVisualCheckpoint && activeVisualMaterial ? (
              <div className="flex flex-1 flex-col overflow-hidden">
                {getLessonVisualCategory(activeVisualMaterial) === 'measurement_text' ? (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-2">
                    <MeasurementTextTrainingVisual
                      readingMaterials={activeMeasurementTextMaterials}
                      questions={activeVisualQuestions}
                      instruction={activeVisualCheckpoint.instruction}
                      formatSeconds={formatSeconds}
                      onComplete={(summary) => setVisualSummary(summary)}
                    />
                  </div>
                ) : getLessonVisualCategory(activeVisualMaterial) === 'question' ? (
                  <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
                    <div className="mt-0">
                      <div className="rounded-2xl border border-[#dbe7ec] bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-extrabold text-[#174e67]">{activeVisualMaterial.title}</p>
                            <p className="mt-0.5 truncate text-xs font-semibold text-[#67808c]">{activeVisualMaterial.pageLabel}</p>
                          </div>
                          <span className="shrink-0 rounded-full bg-[#eef6f8] px-3 py-1 text-[11px] font-black text-[#174e67]">
                            {activeVisualQuestions.length ? `${Math.min(visualQuestionIndex + 1, activeVisualQuestions.length)}/${activeVisualQuestions.length}` : '0/0'}
                          </span>
                        </div>

                        {activeVisualQuestion ? (
                          <>
                            <p className="mt-4 text-base font-black leading-6 text-[#174e67]">{activeVisualQuestion.question}</p>
                            <div className="mt-4 grid gap-2">
                              {activeVisualQuestion.options.map((option, optionIndex) => {
                                const isSelected = visualAnswers[activeVisualQuestion.key] === optionIndex;
                                return (
                                  <button
                                    key={`${activeVisualQuestion.key}_${optionIndex}`}
                                    type="button"
                                    onClick={() => selectVisualAnswer(activeVisualQuestion.key, optionIndex)}
                                    className={`rounded-2xl border px-3 py-3 text-left text-sm font-semibold leading-5 transition ${isSelected
                                        ? 'border-[#0ea5b7] bg-[#e9f6f8] text-[#174e67]'
                                        : 'border-[#dbe7ec] bg-white text-[#1d4758] hover:border-[#0ea5b7] hover:bg-[#f7fbfc]'
                                      }`}
                                  >
                                    <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#eef6f8] text-xs font-black text-[#174e67]">
                                      {String.fromCharCode(65 + optionIndex)}
                                    </span>
                                    {option}
                                  </button>
                                );
                              })}
                            </div>

                            <div className="mt-4 flex justify-end">
                              <button
                                type="button"
                                onClick={() => setVisualQuestionIndex((prev) => Math.min(activeVisualQuestions.length - 1, prev + 1))}
                                disabled={visualQuestionIndex >= activeVisualQuestions.length - 1 || visualAnswers[activeVisualQuestion.key] === undefined}
                                className="rounded-2xl bg-[#174e67] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#0d3a4f] disabled:cursor-not-allowed disabled:bg-[#bfd1d8]"
                              >
                                Sonraki soru
                              </button>
                            </div>
                          </>
                        ) : (
                          <p className="mt-4 rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm font-semibold text-[#a04c4c]">
                            Bu soru görselinden metin soru çıkarılamadı.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : getLessonVisualCategory(activeVisualMaterial) === 'memory_pegs' ? (
                  <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
                    <div className="mt-0">
                      <div className="rounded-2xl border border-[#dbe7ec] bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-extrabold text-[#174e67]">{activeVisualMaterial.title}</p>
                            <p className="mt-0.5 truncate text-xs font-semibold text-[#67808c]">{activeVisualMaterial.pageLabel}</p>
                          </div>
                          <span className="shrink-0 rounded-full bg-[#eef6f8] px-3 py-1 text-[11px] font-black text-[#174e67]">
                            {answeredMemoryPegCount}/{activeMemoryPegEntries.length}
                          </span>
                        </div>

                        <div className="mt-4 grid gap-2 pr-1 sm:grid-cols-2">
                          {getMemoryPegLabels(activeVisualMaterial).map((label) => {
                            const key = getMemoryPegKey(activeVisualMaterial, label);
                            return (
                              <label key={key} className="flex items-center gap-2 rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] px-3 py-2">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-sm font-black text-[#174e67] shadow-sm">
                                  {label}
                                </span>
                                <input
                                  type="text"
                                  value={visualMemoryPegAnswers[key] || ''}
                                  onChange={(event) => updateMemoryPegAnswer(key, event.target.value)}
                                  placeholder="Zihnindeki anlam"
                                  className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[#1d4758] outline-none placeholder:text-[#9fb3bc]"
                                />
                              </label>
                            );
                          })}
                        </div>

                        {activeVisualMaterial.note ? (
                          <p className="mt-3 rounded-2xl bg-[#fff7ed] px-4 py-3 text-sm font-semibold leading-5 text-[#8a521b]">
                            {activeVisualMaterial.note}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : getLessonVisualCategory(activeVisualMaterial) === 'number_tracking' ? (
                  <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
                    <div className="mt-0">
                      <div className="rounded-2xl border border-[#dbe7ec] bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-extrabold text-[#174e67]">{activeVisualMaterial.title}</p>
                            <p className="mt-0.5 truncate text-xs font-semibold text-[#67808c]">{activeVisualMaterial.pageLabel}</p>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <span className="rounded-full bg-[#eef6f8] px-3 py-1 text-[11px] font-black text-[#174e67]">
                              {numberTrackingPhase}. tur
                            </span>
                            <span className={`rounded-full px-3 py-1 text-[11px] font-black ${numberTrackingTimeLeft <= 10 ? 'bg-[#fff2f0] text-[#9a3d32]' : 'bg-[#eef6f8] text-[#174e67]'
                              }`}>
                              {formatSeconds(numberTrackingTimeLeft)}
                            </span>
                          </div>
                        </div>

                        <div className="mt-3">
                          <div className="rounded-2xl bg-[#eef6f8] px-4 py-3">
                            <p className="text-[11px] font-bold uppercase tracking-wide text-[#67808c]">Bulunan</p>
                            <p className="mt-1 text-2xl font-black text-[#174e67]">{activeNumberTrackingFoundCount}/{activeNumberTrackingTotalCount || activeNumberTrackingItems.length}</p>
                          </div>
                        </div>

                        <div className="mt-4 overflow-auto rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-2">
                          <div className="relative mx-auto w-full overflow-hidden rounded-xl bg-white shadow-[0_8px_28px_rgba(23,78,103,0.14)]">
                            <img
                              src={activeVisualMaterial.media?.url}
                              alt={activeVisualMaterial.title}
                              className="block h-auto w-full select-none"
                              draggable={false}
                            />
                            {shouldShowNumberTrackingGuideLines ? (
                              <div
                                className="pointer-events-none absolute bottom-0 top-0 z-20 w-1 -translate-x-1/2 rounded-full bg-[#d91f26] shadow-[0_0_0_1px_rgba(255,255,255,0.8)]"
                                style={{ left: `${activeVisualMaterial.numberTrackingLineXPercent ?? 50}%` }}
                              />
                            ) : null}
                            {shouldShowNumberTrackingGuideLines ? (
                              <div className="pointer-events-none absolute left-0 right-0 top-1/2 z-20 h-1 -translate-y-1/2 rounded-full bg-[#d91f26] shadow-[0_0_0_1px_rgba(255,255,255,0.8)]" />
                            ) : null}
                            {activeNumberTrackingItems.map((item) => {
                              const isSelected = activeNumberTrackingSelections.includes(item.value);
                              return (
                                <button
                                  key={`${activeVisualMaterial.id || activeVisualMaterial.pageLabel}_number_${item.value}`}
                                  type="button"
                                  onClick={() => selectNumberTrackingTarget(item.value)}
                                  disabled={!numberTrackingRunning || numberTrackingTransition || numberTrackingFinished}
                                  className={`absolute z-10 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border text-xs font-black transition ${isSelected
                                      ? 'border-[#7ac99a] bg-[#eefaf2]/90 text-[#21613c]'
                                      : 'border-transparent bg-transparent text-transparent'
                                    }`}
                                  style={{
                                    left: `${item.centerXPercent}%`,
                                    top: `${item.centerYPercent}%`,
                                    width: `${Math.max(26, (item.widthPercent || 5) * 5)}px`,
                                    height: `${Math.max(26, (item.heightPercent || 5) * 5)}px`,
                                  }}
                                  aria-label={`${item.value} sayısını seç`}
                                >
                                  {isSelected ? item.value : ''}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {!activeNumberTrackingItems.length ? (
                          <p className="mt-3 rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm font-semibold text-[#a04c4c]">
                            Bu Numara Yakalama görselinden tıklanabilir sayı konumları çıkarılamadı.
                          </p>
                        ) : numberTrackingFinished ? (
                          <p className="mt-3 rounded-2xl bg-[#eefaf2] px-4 py-3 text-sm font-bold text-[#21613c]">
                            {numberTrackingPhase}. tur tamamlandı. Sonuçları görmek için devam edebilirsin.
                          </p>
                        ) : (
                          <p className="mt-3 rounded-2xl bg-[#eef6f8] px-4 py-3 text-sm font-bold text-[#174e67]">
                            1'den başlayarak sayıları sırayla tıkla. Süre bitince seçimler kilitlenir.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : getLessonVisualCategory(activeVisualMaterial) === 'horizontal_zigzag' ? (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-2">
                    <HorizontalZigzagTrainingVisual
                      bpm={extractHorizontalZigzagBpm(activeVisualCheckpoint, activeVisualMaterial)}
                      durationSec={getHorizontalZigzagDurationSec(activeVisualMaterial)}
                      onOpenMetronome={openMetronomeTab}
                    />
                  </div>
                ) : getLessonVisualCategory(activeVisualMaterial) === 'three_column_grid' ? (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-2">
                    <ThreeColumnGridTrainingVisual
                      bpm={extractHorizontalZigzagBpm(activeVisualCheckpoint, activeVisualMaterial)}
                      durationSec={getHorizontalZigzagDurationSec(activeVisualMaterial)}
                      onOpenMetronome={openMetronomeTab}
                    />
                  </div>
                ) : getLessonVisualCategory(activeVisualMaterial) === 'balloon_reading' ? (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-2">
                    <BalloonReadingTrainingVisual
                      material={activeVisualMaterial}
                      checkpoint={activeVisualCheckpoint}
                    />
                  </div>
                ) : getLessonVisualCategory(activeVisualMaterial) === 'star_reading' ? (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-2">
                    <StarReadingTrainingVisual
                      material={activeVisualMaterial}
                      checkpoint={activeVisualCheckpoint}
                    />
                  </div>
                ) : getLessonVisualCategory(activeVisualMaterial) === 'gap_reading' ? (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-2">
                    <GapReadingTrainingVisual
                      material={activeVisualMaterial}
                      checkpoint={activeVisualCheckpoint}
                    />
                  </div>
                ) : getLessonVisualCategory(activeVisualMaterial) === 'word_alternatives' ? (
                  <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
                    <div className="mt-0">
                      <div className="rounded-2xl border border-[#dbe7ec] bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-extrabold text-[#174e67]">{activeVisualMaterial.title}</p>
                            <p className="mt-0.5 truncate text-xs font-semibold text-[#67808c]">{activeVisualMaterial.pageLabel}</p>
                          </div>
                          <span className="shrink-0 rounded-full bg-[#eef6f8] px-3 py-1 text-[11px] font-black text-[#174e67]">
                            {answeredWordAlternativeCount}/{activeWordAlternativeEntries.length}
                          </span>
                        </div>

                        <div className="mt-3 flex justify-end">
                          <div className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#eef6f8] p-1 text-[#174e67]">
                            <button
                              type="button"
                              onClick={() => changeVisualZoom(-0.15)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-black shadow-sm transition hover:bg-[#f7fbfc]"
                              aria-label="Görseli küçült"
                            >
                              −
                            </button>
                            <span className="w-12 text-center text-[11px] font-black">{Math.round(visualZoom * 100)}%</span>
                            <button
                              type="button"
                              onClick={() => changeVisualZoom(0.15)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-black shadow-sm transition hover:bg-[#f7fbfc]"
                              aria-label="Görseli büyüt"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className={`mt-3 flex items-center justify-center rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-3 ${visualZoom > 1 ? 'overflow-auto' : 'overflow-hidden'}`} style={{ maxHeight: '45vh' }}>
                          <img
                            src={activeVisualMaterial.media?.url}
                            alt={activeVisualMaterial.title}
                            className="block shadow-[0_8px_28px_rgba(23,78,103,0.14)]"
                            style={
                              visualZoom === 1
                                ? { maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }
                                : { width: `${visualZoom * 100}%`, height: 'auto', maxWidth: 'none', maxHeight: 'none' }
                            }
                          />
                        </div>

                        {activeMaterialWordAlternativeEntries.length ? (
                          <div className="mt-4 grid gap-3">
                            {activeMaterialWordAlternativeEntries.map((entry) => {
                              const { exercise } = entry;
                              const blankCount = getWordAlternativeBlankCount(exercise);
                              const selections = Array.from({ length: blankCount }).map((_, index) => (
                                visualWordAlternativeSelections[entry.key]?.[index] ?? null
                              ));
                              const usedOptions = new Set(selections.filter((value): value is number => Number.isInteger(value)));
                              return (
                                <div key={entry.key} className="rounded-2xl bg-[#f7fbfc] p-3">
                                  <div className="flex items-start gap-2 text-sm font-semibold leading-8 text-[#174e67]">
                                    <span className="shrink-0 font-black">{exercise.label})</span>
                                    <p className="min-w-0 flex-1">
                                      {exercise.sentenceParts.map((part, partIndex) => (
                                        <React.Fragment key={`${entry.key}_part_${partIndex}`}>
                                          <span>{part}</span>
                                          {partIndex < blankCount ? (
                                            <button
                                              type="button"
                                              onClick={() => clearWordAlternativeBlank(entry.key, partIndex)}
                                              onDragOver={(event) => event.preventDefault()}
                                              onDrop={(event) => handleWordAlternativeDrop(event, entry.key, exercise, partIndex)}
                                              className={`mx-1 inline-flex min-h-8 min-w-24 items-center justify-center rounded-xl border px-3 py-1 align-middle text-sm font-black transition ${selections[partIndex]
                                                  ? 'border-[#0ea5b7] bg-white text-[#174e67] shadow-sm'
                                                  : 'border-dashed border-[#9fb3bc] bg-white/70 text-[#9fb3bc]'
                                                }`}
                                              aria-label={`${exercise.label} maddesi ${partIndex + 1}. boşluk`}
                                            >
                                              {getWordAlternativeOptionWord(exercise, selections[partIndex]) || '...'}
                                            </button>
                                          ) : null}
                                        </React.Fragment>
                                      ))}
                                    </p>
                                  </div>
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {exercise.options.map((option) => {
                                      const isUsed = usedOptions.has(option.number);
                                      return (
                                        <button
                                          key={`${entry.key}_option_${option.number}`}
                                          type="button"
                                          draggable={!isUsed}
                                          onDragStart={(event) => {
                                            event.dataTransfer.setData('text/plain', String(option.number));
                                          }}
                                          onClick={() => fillNextWordAlternativeBlank(entry.key, exercise, option.number)}
                                          disabled={isUsed}
                                          className={`rounded-full border px-3 py-2 text-sm font-extrabold transition ${isUsed
                                              ? 'cursor-not-allowed border-[#dbe7ec] bg-[#eef6f8] text-[#9fb3bc]'
                                              : 'border-[#cfe2e8] bg-white text-[#174e67] shadow-sm hover:border-[#0ea5b7] hover:bg-[#eef6f8]'
                                            }`}
                                        >
                                          {option.number}. {option.word}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <label className="mt-4 block">
                            <span className="text-xs font-bold uppercase tracking-wide text-[#67808c]">Cevapların</span>
                            <textarea
                              value={visualWordAlternativeAnswers[`${activeVisualMaterial.id || activeVisualMaterial.pageLabel}_fallback`] || ''}
                              onChange={(event) => updateWordAlternativeAnswer(`${activeVisualMaterial.id || activeVisualMaterial.pageLabel}_fallback`, event.target.value)}
                              rows={5}
                              placeholder="Boşluklara gelebileceğini düşündüğün kelimeleri sırayla yaz."
                              className="mt-2 w-full resize-y rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] px-4 py-3 text-sm font-semibold leading-6 text-[#1d4758] outline-none placeholder:text-[#9fb3bc] focus:border-[#0ea5b7] focus:bg-white"
                            />
                          </label>
                        )}

                        {activeVisualMaterial.note ? (
                          <p className="mt-3 rounded-2xl bg-[#fff7ed] px-4 py-3 text-sm font-semibold leading-5 text-[#8a521b]">
                            {activeVisualMaterial.note}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* DEFAULT READING: flex column, image fills remaining height */
                  <div className="flex flex-1 flex-col min-h-0 px-6 pb-2">
                    <div className="shrink-0 flex items-center justify-between gap-3 pb-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold text-[#174e67]">{activeVisualMaterial.title}</p>
                        <p className="mt-0.5 truncate text-xs font-semibold text-[#67808c]">{activeVisualMaterial.pageLabel}</p>
                      </div>
                      <div className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#eef6f8] p-1 text-[#174e67]">
                        <button
                          type="button"
                          onClick={() => changeVisualZoom(-0.15)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-black shadow-sm transition hover:bg-[#f7fbfc]"
                          aria-label="Görseli küçült"
                        >
                          −
                        </button>
                        <span className="w-12 text-center text-[11px] font-black">{Math.round(visualZoom * 100)}%</span>
                        <button
                          type="button"
                          onClick={() => changeVisualZoom(0.15)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-black shadow-sm transition hover:bg-[#f7fbfc]"
                          aria-label="Görseli büyüt"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div
                      className="flex-1 min-h-0 overflow-auto rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] p-2"
                    >
                      <img
                        src={activeVisualMaterial.media?.url}
                        alt={activeVisualMaterial.title}
                        className="block shadow-[0_8px_28px_rgba(23,78,103,0.14)]"
                        style={
                          visualZoom === 1
                            ? { width: '100%', height: 'auto', display: 'block' }
                            : { width: `${visualZoom * 100}%`, height: 'auto', maxWidth: 'none', display: 'block' }
                        }
                      />
                    </div>

                    {activeVisualMaterial.trackTime !== false ? (
                      <p className="shrink-0 mt-2 rounded-2xl bg-[#eef6f8] px-4 py-3 text-sm font-bold text-[#174e67]">
                        Bu sayfada kaldığın süre takip ediliyor.
                      </p>
                    ) : null}

                    {activeVisualMaterial.note ? (
                      <p className="shrink-0 mt-2 rounded-2xl bg-[#fff7ed] px-4 py-3 text-sm font-semibold leading-5 text-[#8a521b]">
                        {activeVisualMaterial.note}
                      </p>
                    ) : null}
                  </div>
                )}

                {!isActiveMeasurementText ? (
                  <div className="shrink-0 border-t border-[#ecf2f5] px-4 py-1.5">
                    <div className="grid grid-cols-[116px,1fr,116px] items-center gap-2">
                      <button
                        type="button"
                        onClick={() => showVisualPage(visualPageIndex - 1)}
                        disabled={visualPageIndex <= 0}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#dbe7ec] bg-white px-3 py-2 text-sm font-bold text-[#174e67] transition hover:bg-[#eef6f8] disabled:cursor-not-allowed disabled:text-[#9fb3bc]"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                        Önceki
                      </button>
                      <button
                        type="button"
                        onClick={continueAfterVisualCheckpoint}
                        disabled={!canContinueVisual}
                        className="rounded-2xl bg-[#174e67] px-5 py-2 text-sm font-bold text-white transition hover:bg-[#0d3a4f] disabled:cursor-not-allowed disabled:bg-[#bfd1d8]"
                      >
                        Devam et
                      </button>
                      <button
                        type="button"
                        onClick={() => showVisualPage(visualPageIndex + 1)}
                        disabled={visualPageIndex >= activeVisualPageCount - 1}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#dbe7ec] bg-white px-3 py-2 text-sm font-bold text-[#174e67] transition hover:bg-[#eef6f8] disabled:cursor-not-allowed disabled:text-[#9fb3bc]"
                      >
                        Sonraki
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center px-8 py-16 text-center">
                <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-[#eef6f8] text-[#0ea5b7]">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18" />
                    <path d="M9 21V9" />
                  </svg>
                </div>
                <p className="text-base font-bold text-[#174e67]">Görsel alan hazır</p>
                <p className="mt-2 max-w-xs text-sm leading-6 text-[#67808c]">
                  Eğitmen yönlendirme yaptığında görsel veya soru çalışması burada açılacak.
                </p>
              </div>
            )}
          </aside>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center bg-[#f7fbfc] px-8 text-center">
            <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-white text-[#0ea5b7] shadow-sm ring-1 ring-[#dbe7ec]">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 11a9 9 0 1 1 18 0v3a9 9 0 1 1-18 0z" />
                <path d="M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z" />
              </svg>
            </div>
            <p className="text-base font-bold text-[#174e67]">Eğitmen sesi çalınıyor</p>
            <p className="mt-2 max-w-xs text-sm leading-6 text-[#67808c]">
              Görsel içerik ve alıştırmalar geldiğinde bu alan aktif olacak.
            </p>
          </div>
        )}

      </div>
      {/* ── END main content row ── */}

      <button
        type="button"
        onClick={openQuestion}
        aria-label="Sormak istediğin var mı?"
        className={`fixed z-30 inline-flex items-center justify-center rounded-full bg-gradient-to-br from-[#0ea5b7] to-[#174e67] text-sm font-bold text-white shadow-[0_18px_45px_rgba(14,165,183,0.45)] transition active:scale-95 ${activeVisualCheckpoint
            ? 'right-7 top-[62%] h-11 w-11 -translate-y-1/2 p-0 sm:right-9'
            : 'bottom-5 right-5 gap-2 px-5 py-3 sm:bottom-8 sm:right-8'
          }`}
      >
        {activeVisualCheckpoint ? (
          <span className="text-lg font-black leading-none">?</span>
        ) : (
          <>
            <span className="relative flex h-3 w-3 items-center justify-center">
              <span className="absolute inset-0 animate-ping rounded-full bg-white/70" />
              <span className="relative h-2 w-2 rounded-full bg-white" />
            </span>
            Sormak istediğin var mı?
          </>
        )}
      </button>

      {activeInteraction ? (
        <div className="fixed bottom-24 left-4 right-4 z-40 mx-auto max-w-2xl overflow-hidden rounded-3xl border border-[#cfe2e8] bg-white shadow-[0_22px_70px_rgba(23,78,103,0.2)] sm:bottom-28">
          <div className="border-b border-[#ecf2f5] bg-[#f7fbfc] px-4 py-3 sm:px-5">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#0ea5b7]">Bölüm sonu sorusu</p>
                {activeInteraction.sectionTitle ? (
                  <p className="mt-0.5 truncate text-xs font-semibold text-[#67808c]">{activeInteraction.sectionTitle}</p>
                ) : null}
              </div>
              <span className="shrink-0 rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[#174e67] shadow-sm">
                {formatSeconds(currentTime)}
              </span>
            </div>
          </div>

          <div className="p-4 sm:p-5">
            <p className="text-base font-extrabold leading-6 text-[#174e67] sm:text-lg">{activeInteraction.question}</p>

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {activeInteraction.options.map((option, index) => {
                const isSelected = selectedOptionIndex === index;
                const isCorrectOption = interactionResult?.correctOptionIndex === index;
                const showCorrect = Boolean(interactionResult) && isCorrectOption;
                const showPendingWrong = !interactionResult && isSelected && index !== activeInteraction.correctOptionIndex;
                const showWrong = Boolean((interactionResult && !interactionResult.correct && isSelected && !isCorrectOption) || showPendingWrong);
                const optionLetter = String.fromCharCode(65 + index);

                return (
                  <button
                    key={`${activeInteraction.id}_${index}`}
                    type="button"
                    onClick={() => handleInteractionAnswer(index)}
                    disabled={interactionLoading || Boolean(interactionResult)}
                    className={`min-h-[58px] rounded-2xl border px-3 py-3 text-left text-sm font-semibold leading-5 transition ${showCorrect
                        ? 'border-[#7ac99a] bg-[#eefaf2] text-[#21613c]'
                        : showWrong
                          ? 'border-[#efb0a8] bg-[#fff2f0] text-[#9a3d32]'
                          : isSelected
                            ? 'border-[#0ea5b7] bg-[#e9f6f8] text-[#174e67]'
                            : 'border-[#dbe7ec] bg-white text-[#1d4758] hover:border-[#0ea5b7] hover:bg-[#f7fbfc]'
                      }`}
                  >
                    <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#eef6f8] text-xs font-black text-[#174e67]">
                      {optionLetter}
                    </span>
                    {option}
                  </button>
                );
              })}
            </div>

            {interactionLoading ? (
              <div className="mt-4 flex items-center gap-2 rounded-2xl bg-[#f7fbfc] px-4 py-3 text-sm font-semibold text-[#67808c]">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#0ea5b7] border-t-transparent" />
                Cevabın kontrol ediliyor…
              </div>
            ) : null}

            {interactionError ? (
              <div className="mt-4 rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm font-semibold text-[#a04c4c]">{interactionError}</div>
            ) : null}

            {interactionResult || showWrongFeedbackAudio ? (
              <div className={`mt-4 rounded-2xl px-4 py-3 ${interactionResult?.correct ? 'bg-[#eefaf2] text-[#21613c]' : 'bg-[#fff7ed] text-[#8a521b]'}`}>
                <p className="text-sm font-bold">
                  {interactionResult?.correct ? 'Doğru cevap. Derse devam edebilirsin.' : 'Bu seçenek doğru değil.'}
                </p>
                {showWrongFeedbackAudio ? (
                  <>
                    <div className="mt-3 h-24 overflow-hidden rounded-2xl bg-[#0b1f29] sm:h-28">
                      <VoiceWaveform isActive={isFeedbackPlaying || Boolean(feedbackAudioSrc)} barCount={40} />
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={replayFeedbackAudio}
                        disabled={!feedbackAudioSrc}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#174e67] shadow-sm transition hover:bg-[#eef6f8] disabled:cursor-not-allowed disabled:text-[#9fb3bc]"
                        aria-label="Açıklama sesini tekrar oynat"
                      >
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9" /><path d="M3 4v5h5" /></svg>
                      </button>
                      {feedbackAudioError ? (
                        <span className="text-xs font-semibold text-[#a04c4c]">{feedbackAudioError}</span>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={continueAfterInteraction}
                disabled={!interactionResult}
                className="rounded-2xl bg-[#174e67] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#0d3a4f] disabled:cursor-not-allowed disabled:bg-[#bfd1d8]"
              >
                Derse devam et
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {visualSummary ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b1f29]/55 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-[0_30px_80px_rgba(11,31,41,0.28)]">
            <div className="border-b border-[#ecf2f5] bg-[#f7fbfc] px-5 py-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0ea5b7]">Çalışma sonucu</p>
              <h3 className="mt-1 text-xl font-black text-[#174e67]">{visualSummary.title}</h3>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-5">
              {visualSummary.pageTimes.length ? (
                <div>
                  <h4 className="text-sm font-black text-[#174e67]">Okuma süreleri</h4>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {visualSummary.pageTimes.length > 1 ? (
                      <div className="rounded-2xl bg-[#174e67] px-4 py-3 text-white">
                        <p className="text-sm font-extrabold">Toplam okuma süresi</p>
                        <p className="mt-2 text-lg font-black">
                          {formatSeconds(visualSummary.pageTimes.reduce((sum, item) => sum + item.seconds, 0))}
                        </p>
                      </div>
                    ) : null}
                    {visualSummary.pageTimes.map((item) => (
                      <div key={`${item.pageLabel}_${item.title}`} className="rounded-2xl bg-[#eef6f8] px-4 py-3">
                        <p className="text-sm font-extrabold text-[#174e67]">{item.title}</p>
                        <p className="mt-0.5 text-xs font-semibold text-[#67808c]">{item.pageLabel}</p>
                        <p className="mt-2 text-lg font-black text-[#174e67]">{formatSeconds(item.seconds)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {visualSummary.questions.length ? (
                <div className="mt-5">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-black text-[#174e67]">Soru sonuçları</h4>
                    <span className="rounded-full bg-[#eef6f8] px-3 py-1 text-xs font-black text-[#174e67]">
                      {visualSummary.questions.filter((item) => item.selectedOptionIndex === item.correctOptionIndex).length}/{visualSummary.questions.length} doğru
                    </span>
                  </div>
                  <div className="mt-3 grid gap-3">
                    {visualSummary.questions.map((item, index) => {
                      const isCorrect = item.selectedOptionIndex === item.correctOptionIndex;
                      return (
                        <div key={item.key} className={`rounded-2xl border px-4 py-3 ${isCorrect ? 'border-[#b8e4c7] bg-[#eefaf2]' : 'border-[#f4c7bd] bg-[#fff2f0]'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm font-extrabold leading-5 text-[#174e67]">{index + 1}. {item.question}</p>
                            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-black ${isCorrect ? 'bg-white text-[#21613c]' : 'bg-white text-[#9a3d32]'}`}>
                              {isCorrect ? 'Doğru' : 'Yanlış'}
                            </span>
                          </div>
                          <p className="mt-2 text-xs font-semibold text-[#67808c]">
                            Seçimin: {item.selectedOptionIndex !== null ? String.fromCharCode(65 + item.selectedOptionIndex) : 'Boş'} · Doğru cevap: {item.correctOptionIndex !== null ? String.fromCharCode(65 + item.correctOptionIndex) : 'Belirtilmedi'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {visualSummary.memoryPegs.length ? (
                <div className="mt-5">
                  <h4 className="text-sm font-black text-[#174e67]">Hafıza Çivileri</h4>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {visualSummary.memoryPegs.map((item) => (
                      <div key={`${item.materialPageLabel}_${item.label}`} className="rounded-2xl bg-[#eef6f8] px-4 py-3">
                        <div className="flex items-start gap-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-sm font-black text-[#174e67]">
                            {item.label}
                          </span>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-[#67808c]">{item.materialTitle}</p>
                            <p className="mt-1 break-words text-sm font-extrabold leading-5 text-[#174e67]">{item.value}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {visualSummary.wordAlternatives.length ? (
                <div className="mt-5">
                  <h4 className="text-sm font-black text-[#174e67]">Kelime Alternatifleri</h4>
                  <div className="mt-3 grid gap-2">
                    {visualSummary.wordAlternatives.map((item) => (
                      <div key={`${item.materialPageLabel}_${item.materialTitle}_${item.label}`} className="rounded-2xl bg-[#eef6f8] px-4 py-3">
                        <p className="text-sm font-extrabold text-[#174e67]">{item.label}) {item.materialTitle}</p>
                        <p className="mt-0.5 text-xs font-semibold text-[#67808c]">{item.materialPageLabel}</p>
                        <p className="mt-2 whitespace-pre-wrap break-words text-sm font-semibold leading-6 text-[#1d4758]">
                          {item.answers.map((answer, index) => `${index + 1}. ${answer || 'Boş'}`).join(' · ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {visualSummary.numberTracking.length ? (
                <div className="mt-5">
                  <h4 className="text-sm font-black text-[#174e67]">Numara Yakalama</h4>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {visualSummary.numberTracking.map((item) => (
                      <div key={`${item.materialPageLabel}_${item.phase}`} className="rounded-2xl bg-[#eef6f8] px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-extrabold text-[#174e67]">{item.phase}. tur</p>
                            <p className="mt-0.5 text-xs font-semibold text-[#67808c]">{item.materialTitle}</p>
                          </div>
                          <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-[#174e67]">
                            {formatSeconds(item.seconds)}
                          </span>
                        </div>
                        <p className="mt-3 text-2xl font-black text-[#174e67]">{item.foundCount}/{item.totalCount}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="border-t border-[#ecf2f5] px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  setVisualSummary(null);
                  advanceAfterVisualCheckpoint();
                }}
                className="w-full rounded-2xl bg-[#174e67] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#0d3a4f]"
              >
                Derse devam et
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showQuestion ? (
        <QuestionDialog
          lessonId={lesson.id}
          currentTimeSec={currentTime}
          onClose={() => setShowQuestion(false)}
          onResume={resumeAfterQuestion}
        />
      ) : null}

      <style>{`
        .speed-reading-lesson-progress { -webkit-appearance: none; appearance: none; height: 6px; border-radius: 999px; background: linear-gradient(90deg, #0ea5b7 0%, #0ea5b7 var(--progress, 0%), #dbe7ec var(--progress, 0%), #dbe7ec 100%); outline: none; }
        .speed-reading-lesson-progress::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; height: 18px; width: 18px; border-radius: 999px; background: #fff; border: 3px solid #174e67; cursor: pointer; box-shadow: 0 4px 18px rgba(23,78,103,0.18); }
        .speed-reading-lesson-progress::-moz-range-thumb { height: 18px; width: 18px; border-radius: 999px; background: #fff; border: 3px solid #174e67; cursor: pointer; box-shadow: 0 4px 18px rgba(23,78,103,0.18); }
      `}</style>
    </div>
  );
};

export default LessonPlayer;
